import { createDebugger } from '../utils/debug.js';
import { filterStructural, classifyAgainstFonts, PICKER_SYMBOLS } from '../utils/engraving-charset.js';
import '../utils/resize-event.js';

// Code-split, opentype.js + @tarikjabiri/dxf only load when the modal opens.
let _dxfModulesPromise = null;
function loadDxfModules() {
    if (!_dxfModulesPromise) {
        _dxfModulesPromise = Promise.all([
            import('../utils/dxf/engraving-dxf.js'),
            import('../utils/dxf/load-font.js')
        ]).then(([dxf, fontLoader]) => ({
            generateEngravingDXFString: dxf.generateEngravingDXFString,
            loadOpentypeFont: fontLoader.loadOpentypeFont,
            getResolvedFont: fontLoader.getResolvedFont,
            createCompositeFont: fontLoader.createCompositeFont
        }));
    }
    return _dxfModulesPromise;
}

const debug = createDebugger('personalize-product', true);

/**
 * @returns {[number, number, number]} hue, lightness and saturation in 0..1.
 */
function rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) return [0, l, 0];

    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
    return [h, l, s];
}

/**
 * @returns {[number, number, number]} r, g and b in 0..255.
 */
function hslToRgb(h, s, l) {
    if (s === 0) {
        const v = Math.round(l * 255);
        return [v, v, v];
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const channel = (t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    return [
        Math.round(channel(h + 1 / 3) * 255),
        Math.round(channel(h) * 255),
        Math.round(channel(h - 1 / 3) * 255)
    ];
}

/**
 * Whether this browser actually applies CanvasRenderingContext2D.filter.
 * Safari, desktop and iOS, exposes the property but ships it disabled, so
 * `'filter' in ctx` is true there and every filter call is a silent no-op.
 * The only honest test is to draw through one and read the pixel back.
 */
let canvasFilterSupport = null;
function canvasFilterWorks() {
    if (canvasFilterSupport !== null) return canvasFilterSupport;
    try {
        const c = document.createElement('canvas');
        c.width = c.height = 2;
        const x = c.getContext('2d');
        x.filter = 'invert(1)';
        x.fillStyle = '#000';
        x.fillRect(0, 0, 2, 2);
        canvasFilterSupport = x.getImageData(0, 0, 1, 1).data[0] > 128;
    } catch {
        canvasFilterSupport = false;
    }
    return canvasFilterSupport;
}

/**
 * Three box blurs on the alpha channel of RGBA pixel data, which is within a
 * few percent of a gaussian of the same radius. Only alpha moves because the
 * halo is one flat color. Stands in for filter: blur() where that is a no-op.
 */
function boxBlurAlpha(data, w, h, radius, passes = 3) {
    if (radius < 1) return;
    const src = new Uint8ClampedArray(w * h);
    for (let i = 0; i < w * h; i++) src[i] = data[i * 4 + 3];
    let a = src;
    let b = new Uint8ClampedArray(w * h);
    const size = radius * 2 + 1;
    for (let pass = 0; pass < passes; pass++) {
        for (let y = 0; y < h; y++) {
            const row = y * w;
            let sum = 0;
            for (let x = -radius; x <= radius; x++) sum += a[row + Math.min(w - 1, Math.max(0, x))];
            for (let x = 0; x < w; x++) {
                b[row + x] = sum / size;
                sum += a[row + Math.min(w - 1, x + radius + 1)] - a[row + Math.max(0, x - radius)];
            }
        }
        for (let x = 0; x < w; x++) {
            let sum = 0;
            for (let y = -radius; y <= radius; y++) sum += b[Math.min(h - 1, Math.max(0, y)) * w + x];
            for (let y = 0; y < h; y++) {
                a[y * w + x] = sum / size;
                sum += b[Math.min(h - 1, y + radius + 1) * w + x] - b[Math.max(0, y - radius) * w + x];
            }
        }
    }
    for (let i = 0; i < w * h; i++) data[i * 4 + 3] = a[i];
}

/**
 * PersonalizeProduct - Interactive canvas for positioning text and logo on product images.
 * Supports draggable text engraving and uploadable logo overlays.
 */
export default class PersonalizeProduct extends HTMLElement {
    constructor() {
        super();

        // Configuration constants
        this.config = {
            // Text styling. Only the debug guides use these now; the engraving
            // itself is drawn by renderEngraving from the values below.
            textColor: '#8D4730',
            textShadowColor: 'rgba(255, 255, 255, 0.8)',
            textShadowBlur: 3,
            textShadowOffsetX: 1,
            textShadowOffsetY: 1,

            // Measured off photographs of real engravings on cognac, brown, green
            // and navy leather. The laser burns the dye off and scorches the
            // collagen underneath, and scorched collagen looks much the same
            // whatever color was on top: across every sample the mark landed at
            // roughly 36-42% lightness with most of the dye's saturation gone,
            // while the leather itself ranged from 4% to 42%. So the burn is an
            // absolute tone, not a tint of the leather, which is why one setting
            // reads darker than pale cognac and lighter than navy without any
            // per-color configuration.
            engrave: {
                // The laser takes the dye off and exposes the hide underneath,
                // and that substrate is brown whatever color was on top. The
                // photographs bear it out: the mark on black leather is a warm
                // copper brown, and so is the one on navy and on green.
                //
                // So the burn is always brown. What changes is which way it goes
                // from the leather. There is only room to burn darker when the
                // hide is light enough to leave any, so cognac takes a dark rich
                // mark while brown, navy and black take a lighter one. That is
                // the same rule producing what looked like two behaviours.
                substrateHue: 16 / 360,
                substrateSaturation: 0.5,
                // A little of the hide's own intensity carries through, which
                // keeps the mark on cognac rich rather than muddy.
                saturationFromLeather: 0.45,
                maxSaturation: 0.62,

                // Deliberately past what the photographs show. A preview nobody
                // can read is worse than one that flatters the burn.
                darkSeparation: 0.16,
                lightSeparation: 0.17,
                minLightness: 0.385,
                // Only go darker when doing so still leaves the mark off the
                // floor, otherwise there is nothing to see. Kept below
                // darkSeparation on purpose: raise it above and a hide that
                // should burn dark gets bounced onto the light path instead,
                // which sent cognac to 50 percent lightness, brighter than the
                // setting it was meant to deepen.
                darkFloor: 0.125,

                // How much texture ends up in the letters, as a target spread of
                // luminance rather than a multiplier. A multiplier gives whatever
                // the patch happens to hold, so a mark on a smooth panel comes out
                // flat while the same setting on a coarse hide comes out busy. The
                // amount is measured and matched instead, so every color and every
                // position carries the same grain.
                // Calibrated to the black hide, which carried the most texture of
                // the five and was the one that read best. Under the old
                // multiplier the amount ranged from 60 on cognac to 97 on black,
                // purely because it inherited whatever the patch held. Every
                // color is brought to the same figure now.
                grainTarget: 60,
                grainContrastMin: 0.4,
                grainContrastMax: 6,
                // In device pixels, not CSS pixels. At DPR 2-3.5 a CSS pixel is 2-3
                // device pixels, and a rim that wide stopped reading as an edge:
                // enlarged, each pass showed as its own grey copy of the word
                // beside the patterned one (client report, Aug 2026).
                rimOffset: 1,
                rimDark: 0.18,
                rimLight: 0.4,
                halo: 0.1,
                haloBlur: 0.13,
                // 'auto' uses canvas filters where the browser applies them and
                // the per-pixel path where it does not (Safari). 'always' forces
                // the pixel path everywhere, for comparing the two side by side.
                pixelPath: 'auto',
                // Below this on-screen size the grain and halo are wider than
                // the strokes and the word turns to mush, so the mark is drawn
                // flat: burn tone plus rim only.
                flatBelowPx: 16
            },

            // A monogram is pressed into the leather with heated metal type,
            // optionally through a foil. Nothing is burnt, so the mark keeps
            // the leather's own color when no foil is chosen and takes the
            // foil's when one is. What sells the impression is depth: the wall
            // nearest the light sits in shadow and the far wall catches it,
            // the opposite emphasis from the engraving's rims because the
            // surface goes down rather than being taken away.
            deboss: {
                // In device pixels, like the engraving rims.
                rimOffset: 1,
                rimDark: 0.5,
                rimLight: 0.35,
                // How much darker the pressed leather reads with no foil, in
                // lightness. Heat and pressure compress the grain and it goes
                // a shade deeper and glossier.
                blindDarken: 0.1,
                // Foils are drawn with a short vertical gradient so they read
                // as metal rather than as flat paint.
                foils: {
                    gold: { top: 'rgb(232, 200, 96)', mid: 'rgb(184, 142, 40)', bottom: 'rgb(214, 176, 70)' },
                    silver: { top: 'rgb(236, 236, 240)', mid: 'rgb(176, 176, 184)', bottom: 'rgb(214, 214, 220)' },
                    black: { top: 'rgb(40, 36, 32)', mid: 'rgb(18, 16, 14)', bottom: 'rgb(30, 27, 24)' }
                }
            },

            // The monogram press: one size, one face, five characters, and
            // only what the type case holds.
            monogram: {
                // The height of the pressed capitals, 8mm, confirmed by
                // Vaidas Sep 2026. He first said "roughly 1cm", corrected it
                // to 0.8cm after measuring the press, and confirmed it is the
                // letter height rather than the font size.
                //
                // Not a font size. Georgia's capitals fill about 69% of the
                // em, so setting the font to 8mm would press letters 5.5mm
                // tall, a third short. The font size is derived from this.
                capHeightMm: 8,
                // Only used if the measurement comes back empty, which means
                // no font is resolvable at all. Georgia's real figure.
                fallbackCapHeightRatio: 0.693,
                maxChars: 5,
                allowedChar: /^[A-Z0-9.]$/
            },

            // Font sizing, in millimetres on the product. The slider used to be
            // in canvas pixels, so one slider position meant about 7 mm on a
            // desktop canvas and about 20 mm on a phone, the character limit
            // followed the screen, and so did the DXF the laser received.
            minFontSizeMm: 4,
            maxFontSizeMm: 40,
            // The slider's ceiling depends only on the font and the character
            // count, not the product, and sits at 22 mm for Engravers MT with
            // the five characters an empty field is measured for. 14 mm puts
            // the thumb a little past halfway, so the text is easy to find on
            // the photo the moment the dialog opens (client note, Sep 2026).
            // It was 8 mm, a fifth of the way along.
            defaultFontSizeMm: 14,
            fontSizeStepMm: 0.5,

            // Snap the canvas request to a small fixed set instead of the raw
            // canvas width. The point is not to match the gallery: callers
            // pass their own width lists all over this theme (390/719/1200,
            // 400/600/800, 300/600/900 and more), so there is no single set to
            // align with and no browser cache hit to rely on. The point is
            // that an unsnapped width is viewport times DPR, so every device
            // asks for its own one-off variant that the CDN has to generate
            // from a 2000px source. Six shared widths keep those variants warm
            // across visitors instead.
            imageWidthSteps: [480, 720, 960, 1280, 1600, 2048],

            // Physical constraints for laser engraving
            maxEngravingWidthCm: 13, // Maximum width of engraving in centimeters
            pixelsPerCm: 26, // Conversion ratio (96 DPI = ~37.8 pixels per cm)
            minCharacterCount: 5, // Minimum characters allowed
            sizeHintGap: 10, // Space between the size note and whatever it points at, leaving room for its arrow
            averageCharWidthRatio: 0.6, // Average char width as ratio of font size (for monospace-ish fonts)

            // Logo constraints (in cm)
            maxLogoWidthCm: 8,
            maxLogoHeightCm: 8,

            // Interaction
            hitAreaPadding: 10, // Padding around elements for easier dragging

            // Rotation of the engraving as a whole, in degrees clockwise
            defaultTextRotation: 0,
            // Gap between characters in stacked mode, as a multiple of font size
            stackLineHeightRatio: 1.2,

            // Default positions (percentages)
            // Where the text lands before the customer moves it, as a
            // percentage of the product photo rather than of the canvas, so a
            // wide photo letterboxed on a tall canvas still puts it on the
            // leather. A quarter of the way up from the bottom: most pieces
            // are engraved low, near a lock or a seam, and starting there
            // meant less dragging (client note, Sep 2026).
            defaultTextImagePosition: { x: 50, y: 75 },
            // Canvas-relative stand-in until the photo has loaded.
            defaultTextPosition: { x: 50, y: 50 },
            defaultLogoPosition: { x: 50, y: 40 },
            defaultLogoScale: 0.5, // 50% of max cm size

            // The dashed frame around the text and the Move handle under it,
            // there so a first-time customer can see the text is a thing that
            // moves. The frame stays until they take hold of it once, then
            // only shows while the pointer is over the text or the handle.
            hint: {
                padding: 8,
                fadeMs: 220,
                handleGap: 10,
                // How long the frame lingers after a finger lifts, since
                // there is no hover on a touch screen to keep it up.
                touchLingerMs: 700
            },

            // Upload settings
            uploadMaxSize: 2000, // Max pixel dimension for the uploaded mockup; the canvas is already DPR-capped at 2, so this only trims very wide desktop layouts
            uploadJpegQuality: 0.92, // JPEG compression quality (0–1)

            // Debug mode - shows measurement guides (green line, measurement boxes, stats)
            debugMode: false
        };

        this.canvas = null;
        this.ctx = null;
        this.productImage = new Image();
        this.resizeObserver = null;

        // Applied state. _appliedVariantId is the variant the engraving on the
        // form was drawn against, so a later color change can be spotted;
        // _needsRevalidation holds Add to Cart until the customer has looked at
        // the engraving on the new color.
        this._appliedVariantId = null;
        this._needsRevalidation = false;
        this._preserveTextPosition = false;

        // Text state
        this.textPosition = { ...this.config.defaultTextPosition };
        this.currentFontSizeMm = this.config.defaultFontSizeMm;
        this.currentFontSize = (this.currentFontSizeMm / 10) * this.config.pixelsPerCm; // px twin, kept in step by syncFontSizePx()
        this.previousFont = null;

        // Rotation state. textRotation is 0/90/180/270 clockwise; stackLetters
        // keeps each glyph upright and lays them out along the rotated axis
        // instead of turning the whole block.
        this.textRotation = this.config.defaultTextRotation;
        this.stackLetters = false;

        // Logo state
        this.logoImage = null;
        this.logoImageUrl = null;
        this.logoPosition = { ...this.config.defaultLogoPosition };

        this.logoScale = this.config.defaultLogoScale;

        // Drag state, null means not dragging, 'text'|'logo' means which element
        this.dragTarget = null;
        this.dragOffset = { x: 0, y: 0 };

        // Drag hint state. Pinned means the customer has not taken hold of
        // the text yet, so the frame stays up regardless of the pointer.
        this._hintPinned = true;
        this._hintAlpha = 0;
        this._hintTarget = 0;
        this._hintRaf = null;
        this._hintLastTs = 0;
        this._hintOverHandle = false;
        // Spent by the resubmit that confirming the position check triggers.
        this._positionConfirmed = false;
        this._resetPositionOnLoad = false;
        this._exporting = false;
        this.moveHandle = null;

        // Width in cm of the current image, null = use hardcoded pixelsPerCm
        this.currentImageCm = null;

        // Where the press lands, as a percentage of the location image rather
        // than of the canvas, so the spot is the same on a phone and a desktop
        // whatever the letterboxing. Set per location in the admin.
        this.monogramImagePos = { x: 50, y: 50 };

        // Bind all handlers once for stable add/remove
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this.handleFontChange = this.handleFontChange.bind(this);
        this.handleTextChange = this.handleTextChange.bind(this);
        this.handleSizeChange = this.handleSizeChange.bind(this);
        this.handleRotationChange = this.handleRotationChange.bind(this);
        this.handleStackChange = this.handleStackChange.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);
        this.handleAddToCartGuard = this.handleAddToCartGuard.bind(this);
        this.handleSizeHintToggle = this.handleSizeHintToggle.bind(this);
        this.handleControlsScroll = this.handleControlsScroll.bind(this);
        this.handleSizeHintOutsideClick = this.handleSizeHintOutsideClick.bind(this);
        this.handleProductFormSubmitGuard = this.handleProductFormSubmitGuard.bind(this);
        this.handleLocationChange = this.handleLocationChange.bind(this);
        this.handleApply = this.handleApply.bind(this);
        this.handlePositionConfirm = this.handlePositionConfirm.bind(this);
        this.handlePositionMove = this.handlePositionMove.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleLogoUpload = this.handleLogoUpload.bind(this);
        this.handleReloadOnEvent = this.handleReloadOnEvent.bind(this);
        this.handleSymbolClick = this.handleSymbolClick.bind(this);
        this.handleFontSuggestionClick = this.handleFontSuggestionClick.bind(this);
        this.handleRemoveRequest = this.handleRemoveRequest.bind(this);
        this.handleColorChange = this.handleColorChange.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        this.handleHandlePointerDown = this.handleHandlePointerDown.bind(this);
        this.handleHandlePointerMove = this.handleHandlePointerMove.bind(this);
        this.handleHandlePointerUp = this.handleHandlePointerUp.bind(this);
        this.handleHandleEnter = this.handleHandleEnter.bind(this);
        this.handleHandleLeave = this.handleHandleLeave.bind(this);
        this.animateHint = this.animateHint.bind(this);

        this._measureCache = new Map();
    }

    /**
     * 'engraving' or 'monogram', decided by the product's personalization page
     * and passed in by the section. A monogram has no font choice, no size
     * slider, no rotation, no logo and no DXF; the preview is a deboss and the
     * spot is fixed.
     */
    isMonogram() {
        return this.dataset.mode === 'monogram';
    }

    async connectedCallback() {
        await this.waitForFonts();
        this.init();
        this.prewarmLocationImage();
    }

    /**
     * Fetch the photo the canvas will draw, before the customer opens the
     * dialog, so it comes off the browser cache instead of the network.
     *
     * The location photos are not the gallery's photos, so nothing on the page
     * has requested them at canvas size and the CDN has no variant cached at
     * that width either. Opening the dialog was the first request for that
     * image at that size, which is what made it three to five seconds.
     *
     * Only the checked location: the rest cost a request each and most
     * customers never switch. Deferred to idle, after load, so it never
     * competes with the page's own images (client note, Sep 2026).
     */
    prewarmLocationImage() {
        const input = this.querySelector('[data-location-select]:checked');
        const src = input?.dataset.url;
        if (!src) return;

        const start = () => {
            const img = new Image();
            // The canvas needs it CORS-clean to read pixels back for the
            // mockup, and a cache entry without that header would not be
            // reusable there, so warm it the same way it will be used.
            img.crossOrigin = 'anonymous';
            // Deliberately not fetchPriority 'low'. That reads as the polite
            // choice for a speculative fetch, and it was measurably the wrong
            // one here: low took 828ms against 252ms at default on a settled
            // page, and far worse on a busy one. Idle timing already keeps
            // this out of the page's way; starving it as well only meant the
            // dialog opened before its own prewarm had finished.
            img.src = this.getOptimalImageUrl(src);
            debug.log('prewarmLocationImage:', { src: img.src });
        };

        // Deliberately not hooked to the load event. This storefront has a
        // third-party request that never settles, so load does not fire here
        // at all and a prewarm waiting on it would simply never run. Verified
        // in the network log, and the screenshot tool reports the same thing.
        // requestIdleCallback already yields to the page's own work, which is
        // the part that actually mattered.
        if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 3000 });
        else setTimeout(start, 1200);
    }

    disconnectedCallback() {
        this.cleanup();
    }

    init() {
        this.canvasContainer = this.querySelector('[data-canvas-container]');
        this.fontInputs = this.querySelectorAll('input[name="font"]');
        this.textInput = this.querySelector('#engraving-message');
        this.specialNotesInput = this.querySelector('#special-notes');
        this.symbolPicker = this.querySelector('[data-symbol-picker]');
        this.sizeSlider = this.querySelector('#text-size');
        this.rotationInputs = this.querySelectorAll('[data-rotation-select]');
        this.orientationInputs = this.querySelectorAll('[data-orientation-select]');
        this.stackWrapper = this.querySelector('[data-stack-wrapper]');
        this.stackSample = this.querySelector('[data-stack-sample]');
        this.charCounter = this.querySelector('[data-char-counter]');
        this.sizeHint = this.querySelector('[data-size-hint]');
        this.sizeHintBtn = this.querySelector('[data-size-hint-btn]');
        this.sizeHintClose = this.querySelector('[data-size-hint-close]');
        this.sizeHintArrow = this.querySelector('[data-size-hint-arrow]');
        this.charWarning = this.querySelector('[data-char-warning]');
        this.charsetWarning = this.querySelector('[data-charset-warning]');
        this.fontSuggestion = this.querySelector('[data-font-suggestion]');
        this.fontSuggestionText = this.querySelector('[data-font-suggestion-text]');
        this.fontSuggestionName = this.querySelector('[data-font-suggestion-name]');
        this.fontSuggestionBtn = this.querySelector('[data-font-suggestion-btn]');
        this.form = this.querySelector('form');
        this.applyBtn = this.querySelector('[data-apply-btn]');
        this.applyBtnText = this.querySelector('[data-apply-btn-text]');
        this.applyBtnDefaultText = this.applyBtnText?.textContent || '';
        this.positionCheck = this.querySelector('[data-position-check]');
        this.positionCheckPanel = this.querySelector('[data-position-check-panel]');
        this.positionCheckConfirm = this.querySelector('[data-position-check-confirm]');
        this.positionCheckMove = this.querySelector('[data-position-check-move]');
        this.logoUploadBtn = this.querySelector('[data-logo-upload-btn]');
        this.logoRemoveBtn = this.querySelector('[data-logo-remove-btn]');
        this.logoSizeSlider = this.querySelector('[data-logo-size]');
        this.logoFileInput = this.querySelector('[data-logo-file-input]');
        this.colorInputs = this.querySelectorAll('[data-color-select]');

        if (!this.canvasContainer) {
            debug.error('Canvas container not found, cannot initialize');
            return;
        }

        this.createCanvas();
        this.initLocationSelector();
        this.addEventListeners();
        this.initializeSizeControls();
        this.updateStackVisibility();
        this.previousFont = this.getSelectedFont();

        if (this.isMonogram()) {
            // One size for the press. There is no slider to read it from, so
            // it is set here and the counter is derived from it.
            this.applyMonogramSize();
            this.ensureFontLoaded(this.getSelectedFont());
            this.updateCharacterCounter();
        }

        this.render();

        // The laser needs a cut file and a glyph-coverage check across every
        // font. The press needs neither: Georgia carries every character the
        // monogram gate lets through, so opentype.js stays unloaded.
        if (!this.isMonogram()) {
            this.prewarmSelectedFontForDXF();
            this.prewarmAllFontsForValidation();
        }
        this.maybeEnterEditMode();
    }

    /**
     * If the URL carries ?personalize=<line_key>, prefill the modal from that
     * cart line item and open it. Used for "Edit personalization" links coming
     * back from the cart.
     */
    maybeEnterEditMode() {
        const lineKey = new URLSearchParams(window.location.search).get('personalize');
        if (!lineKey) return;

        debug.log('maybeEnterEditMode: looking for line', { lineKey });

        try {
            const candidates = document.querySelectorAll('[data-engraving-line]');
            debug.log('maybeEnterEditMode: candidates found', {
                count: candidates.length,
                keys: Array.from(candidates).map((el) => el.dataset.lineKey)
            });

            const dataEl = Array.from(candidates).find((el) => el.dataset.lineKey === lineKey);
            if (!dataEl) {
                debug.warn('maybeEnterEditMode: no matching line for this product', { lineKey });
                return;
            }

            const engravingRaw = dataEl.dataset.engraving;
            debug.log('maybeEnterEditMode: engraving payload', {
                length: engravingRaw?.length,
                preview: engravingRaw?.slice(0, 200)
            });

            this.editLineKey = lineKey;
            this.editQuantity = parseInt(dataEl.dataset.quantity, 10);
            this.editVariantId = parseInt(dataEl.dataset.variantId, 10);
            this.prefillFromEngraving(JSON.parse(engravingRaw));
            this.switchApplyButtonToSave();
            this.openModal();
        } catch (err) {
            debug.error('maybeEnterEditMode: failed', { err });
        }
    }

    prefillFromEngraving(data) {
        if (this.textInput && data.message) this.textInput.value = data.message;

        if (data.font) {
            const fontInput = this.querySelector(`input[name="font"][value="${CSS.escape(data.font)}"]`);
            if (fontInput) {
                fontInput.checked = true;
                this.previousFont = data.font;
                // Checked by hand, so the dropdown trigger is told to catch up.
                fontInput.dispatchEvent(new CustomEvent('custom-select:sync', { bubbles: true }));
            }
        }

        // Saved sizes are in mm. Items saved before the mm change carry only a
        // pixel size; converting it with this device's scale is the best guess.
        if (data.fontSizeMm) this.setFontSizeMm(data.fontSizeMm);
        else if (data.fontSize) this.setFontSizeMm(this.pxToMm(data.fontSize));

        // Restore rotation before updateSizeSlider() runs at the end, so the
        // size cap is derived from the layout the customer actually saved.
        this.textRotation = this.normalizeRotation(data.rotation ?? this.config.defaultTextRotation);
        const rotationInput = this.querySelector(`[data-rotation-select][value="${this.textRotation}"]`);
        if (rotationInput) rotationInput.checked = true;

        this.stackLetters = !!data.stackLetters;
        const orientationInput = this.querySelector(
            `[data-orientation-select][value="${this.stackLetters ? 'stacked' : 'block'}"]`
        );
        if (orientationInput) orientationInput.checked = true;
        this.updateStackVisibility();

        // selectLocation resets textPosition to default, set location FIRST,
        // then restore the saved position so it doesn't get clobbered.
        if (data.location) {
            const locInput = this.querySelector(
                `input[data-location-select][value="${CSS.escape(data.location)}"]`
            );
            if (locInput) {
                locInput.checked = true;
                this.selectLocation(locInput);
            }
        }
        // A monogram's spot comes from the location, never from the saved line,
        // so a position moved in the admin since the order reaches the edit.
        if (data.position && !this.isMonogram()) {
            this.textPosition = { ...data.position };
            // selectLocation above queued a reset for when the photo lands;
            // the saved spot wins over the default.
            this._resetPositionOnLoad = false;
        }
        if (data.message) this.setHint(true);

        // `color` for lines already in a cart when the key was renamed.
        const savedColor = data.color ?? data.color;
        if (savedColor) {
            const colorInput = this.querySelector(
                `input[name="monogram-color"][value="${CSS.escape(savedColor)}"]`
            );
            if (colorInput) colorInput.checked = true;
        }

        if (data.specialNotes && this.specialNotesInput) {
            this.specialNotesInput.value = data.specialNotes;
        }

        if (data.logoUrl) {
            this.logoImageUrl = data.logoUrl;
            this.logoImage = new Image();
            this.logoImage.crossOrigin = 'anonymous';
            this.logoImage.onload = () => this.render();
            this.logoImage.src = data.logoUrl;
            if (data.logoPosition) this.logoPosition = { ...data.logoPosition };
            if (this.logoUploadBtn)
                this.logoUploadBtn.textContent = this.dataset.changeLogoText || 'Change logo';
            if (this.logoRemoveBtn) this.logoRemoveBtn.style.display = '';
            const sizeWrapper = this.logoSizeSlider?.closest('[data-logo-size-wrapper]');
            if (sizeWrapper) sizeWrapper.style.display = '';
        }

        this.updateSizeSlider();
        this.updateCharacterCounter();
        this.render();
    }

    switchApplyButtonToSave() {
        if (!this.applyBtnText) return;
        const saveText = this.dataset.saveText || 'Save & Update Cart';
        this.applyBtnText.textContent = saveText;
        this.applyBtnDefaultText = saveText;
    }

    openModal() {
        const modal = this.closest('modal-dialog');
        if (modal && typeof modal.open === 'function') {
            modal.open();
        } else if (modal) {
            modal.setAttribute('open', '');
        }
    }

    setApplyBusy(busy) {
        if (!this.applyBtn) return;
        this.applyBtn.disabled = busy;
        this.applyBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
        // A label change alone made a slow upload read as a dead button.
        this.applyBtn.classList.toggle('is-loading', busy);
        if (this.applyBtnText) {
            const busyText = this.editLineKey
                ? this.dataset.savingText || 'Saving…'
                : this.dataset.uploadingText || 'Uploading…';
            this.applyBtnText.textContent = busy ? busyText : this.applyBtnDefaultText;
        }
    }

    prewarmSelectedFontForDXF() {
        const input = this.querySelector('input[name="font"]:checked') || this.fontInputs[0];
        const url = input?.dataset.fontUrl;
        if (!url) return;
        loadDxfModules()
            .then(({ loadOpentypeFont }) => loadOpentypeFont(url))
            .catch((err) => debug.warn('prewarmSelectedFontForDXF: load failed', { url, err }));
    }

    /**
     * The character gate has to answer on every keystroke, so it cannot wait on a
     * fetch. Every engraving font is pulled in once when the modal opens and kept
     * here; until they arrive only the structural gate runs, which is the cheap
     * one and catches emoji anyway.
     */
    prewarmAllFontsForValidation() {
        this._fontEntries = this._fontEntries || new Map();
        const urls = Array.from(this.fontInputs)
            .map((input) => input.dataset.fontUrl)
            .filter(Boolean);

        loadDxfModules()
            .then(({ loadOpentypeFont, getResolvedFont }) =>
                Promise.all(
                    urls.map((url) => {
                        const ready = getResolvedFont(url);
                        if (ready) {
                            this._fontEntries.set(url, ready);
                            return null;
                        }
                        return loadOpentypeFont(url)
                            .then((font) => this._fontEntries.set(url, font))
                            .catch((err) => debug.warn('prewarmAllFonts: load failed', { url, err }));
                    })
                )
            )
            .then(() => {
                debug.log('prewarmAllFonts: ready', { count: this._fontEntries.size });
                // Text may already be present when re-opening to edit a cart line.
                if (this.textInput?.value) this.updateFontSuggestion(this.textInput.value);
            })
            .catch((err) => debug.warn('prewarmAllFonts: failed', { err }));
    }

    createCanvas() {
        this.canvas = document.createElement('canvas');
        // touch-none stops the page scrolling under a drag. With nothing to
        // drag it would only stop the customer scrolling the dialog.
        this.canvas.className = this.isMonogram() ? 'w-full h-full' : 'w-full h-full cursor-move touch-none';
        this.canvas.setAttribute('role', 'img');
        this.canvas.style.letterSpacing = '0px';
        this.canvas.setAttribute(
            'aria-label',
            this.isMonogram()
                ? 'Product preview with your monogram in place.'
                : 'Product preview with personalization. Use mouse or touch to drag elements to desired position.'
        );
        this.ctx = this.canvas.getContext('2d');

        this.canvasContainer.innerHTML = '';
        this.canvasContainer.appendChild(this.canvas);
        if (!this.isMonogram()) this.createMoveHandle();

        this.resizeCanvas();
    }

    /**
     * A button that hangs off the bottom of the text and drags it. It is DOM,
     * not canvas, so it can be styled like the rest of the form and never
     * ends up in the mockup JPEG. render() keeps it under the text.
     */
    createMoveHandle() {
        const label = this.canvasContainer.dataset.moveLabel || 'Move';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className =
            'absolute z-10 hidden -translate-x-1/2 items-center gap-1.5 px-3 py-2 bg-black text-white text-xs leading-none rounded shadow-md cursor-grab active:cursor-grabbing touch-none select-none transition-colors hover:bg-neutral-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black';
        btn.setAttribute('data-move-handle', '');
        btn.setAttribute('aria-label', label);
        btn.setAttribute('title', label);
        btn.innerHTML =
            '<span class="absolute left-1/2 -top-2.5 h-2.5 w-px bg-black" aria-hidden="true"></span>' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20"/></svg>' +
            '<span></span>';
        btn.lastElementChild.textContent = label;

        btn.addEventListener('pointerdown', this.handleHandlePointerDown);
        btn.addEventListener('pointermove', this.handleHandlePointerMove);
        btn.addEventListener('pointerup', this.handleHandlePointerUp);
        btn.addEventListener('pointercancel', this.handleHandlePointerUp);
        btn.addEventListener('pointerenter', this.handleHandleEnter);
        btn.addEventListener('pointerleave', this.handleHandleLeave);

        this.canvasContainer.appendChild(btn);
        this.moveHandle = btn;
    }

    handleHandlePointerDown(e) {
        if (!this.textInput?.value) return;
        e.preventDefault();
        this.moveHandle.setPointerCapture?.(e.pointerId);
        this._lastPointerType = e.pointerType;
        const { x, y } = this.getCanvasPoint(e.clientX, e.clientY);
        this.startDrag('text', x, y);
    }

    handleHandlePointerMove(e) {
        if (this.dragTarget !== 'text') return;
        e.preventDefault();
        const { x, y } = this.getCanvasPoint(e.clientX, e.clientY);
        this.moveDrag(x, y);
    }

    handleHandlePointerUp(e) {
        if (!this.dragTarget) return;
        this.moveHandle.releasePointerCapture?.(e.pointerId);
        this.endDrag();
        if (e.pointerType === 'touch') this.lingerHint();
    }

    handleHandleEnter(e) {
        if (e.pointerType === 'touch') return;
        this._hintOverHandle = true;
        this.setHint(true);
    }

    handleHandleLeave(e) {
        if (e.pointerType === 'touch') return;
        this._hintOverHandle = false;
        if (!this.dragTarget) this.setHint(false);
    }

    /**
     * Put the handle under the text, or hide it when there is nothing to move.
     * Runs at the end of every render, so it follows a drag and a resize.
     */
    updateMoveHandle() {
        const btn = this.moveHandle;
        if (!btn) return;

        const text = this.textInput?.value || '';
        if (!text || !this.canvas?.width) {
            btn.classList.add('hidden');
            btn.classList.remove('flex');
            return;
        }

        const rect = this.canvas.getBoundingClientRect();
        const { h } = this.getTextDisplaySize();
        const cfg = this.config.hint;
        const tx = (this.textPosition.x / 100) * rect.width;
        const ty = (this.textPosition.y / 100) * rect.height;

        // Keep the button on the canvas near the edges. Below the text unless
        // that would fall off the bottom, in which case it sits above.
        const below = ty + h / 2 + cfg.padding + cfg.handleGap;
        const fitsBelow = below + 34 <= rect.height;
        const top = fitsBelow ? below : Math.max(0, ty - h / 2 - cfg.padding - cfg.handleGap - 30);
        const left = Math.min(rect.width - 44, Math.max(44, tx));

        btn.style.left = `${left}px`;
        btn.style.top = `${top}px`;
        btn.firstElementChild.classList.toggle('hidden', !fitsBelow);
        btn.classList.remove('hidden');
        btn.classList.add('flex');

        // Same rule as the frame: up until the first drag, then only while the
        // pointer is over the text or the handle, fading together. Faded out
        // it must not catch the pointer, or it would block the leather under
        // it and never let the text's own hover bring it back.
        btn.style.opacity = this._hintAlpha.toFixed(3);
        btn.style.pointerEvents = this._hintAlpha < 0.05 ? 'none' : '';
        btn.setAttribute('aria-hidden', this._hintAlpha < 0.05 ? 'true' : 'false');
    }

    resizeCanvas() {
        if (!this.canvas || !this.canvasContainer) {
            debug.warn('resizeCanvas: canvas or container missing');
            return;
        }

        const rect = this.canvasContainer.getBoundingClientRect();
        const dpr = Math.min(window.devicePixelRatio || 1, 2);

        if (rect.width === 0 || rect.height === 0) {
            debug.warn('resizeCanvas: container has zero dimensions', {
                width: rect.width,
                height: rect.height
            });
        }

        this.canvas.width = rect.width * dpr;
        this.canvas.height = rect.height * dpr;
        this.canvas.style.width = `${rect.width}px`;
        this.canvas.style.height = `${rect.height}px`;

        this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        this.syncFontSizePx();
        this.render();
    }

    initLocationSelector() {
        const inputs = this.querySelectorAll('[data-location-select]');
        const checked = this.querySelector('[data-location-select]:checked') || inputs[0];

        debug.log('initLocationSelector:', {
            inputsFound: inputs.length,
            checkedValue: checked?.value,
            dataUrl: checked?.dataset.url,
            dataWidth: checked?.dataset.width
        });

        if (checked) {
            this.selectLocation(checked);
        } else {
            debug.warn('initLocationSelector: no [data-location-select] inputs found in DOM');
        }
    }

    getOptimalImageUrl(src, overrideWidth = null) {
        const physicalWidth =
            overrideWidth || this.canvas?.width || this.predictCanvasWidth();

        const absoluteSrc = src.startsWith('//') ? `https:${src}` : src;

        try {
            const url = new URL(absoluteSrc);
            url.searchParams.set('width', this.snapImageWidth(physicalWidth));
            return url.toString();
        } catch {
            return absoluteSrc;
        }
    }

    /**
     * The canvas width in device pixels, worked out from the dialog's CSS
     * rather than measured.
     *
     * Needed because the prewarm runs while the dialog is still display:none,
     * where everything measures zero. Predicting it the same way the layout
     * computes it means the prewarm asks for the same URL the canvas will ask
     * for on open, which is the only thing that makes the prewarm worth doing.
     *
     * Mirrors personalization-popup.liquid: the dialog is max-w-6xl (72rem)
     * with mx-6 and p-8 from md up, where the grid is 3fr_2fr with a gap-8, and
     * below md it is full width with an 18px gutter and one column.
     */
    predictCanvasWidth() {
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const vw = window.innerWidth || 1280;
        let css;
        if (vw >= 768) {
            const content = Math.min(1152, vw - 48) - 64;
            css = ((content - 32) * 3) / 5;
        } else {
            css = vw - 36;
        }
        return Math.ceil(Math.max(css, 320) * dpr);
    }

    /**
     * Round a pixel width up to one of the widths the theme already asks for.
     *
     * The canvas width times DPR is an arbitrary number that differs per
     * viewport and per device: 1584, 1622, 1728. Shopify's CDN generates each
     * distinct `width` variant on first request, so an arbitrary one is a
     * variant nobody has ever asked for and the first visitor on that viewport
     * waits out a cold resize of a 2000px source. That was three to five
     * seconds of blank canvas.
     *
     * snippets/image.liquid uses 480, 640, 960 and 1280 for every srcset, so
     * those are warm on the CDN, and for a photo the gallery already rendered
     * they are sitting in the visitor's browser cache. Asking for the same URL
     * the page already fetched costs nothing at all.
     *
     * Always up, never down: the mockup JPEG that goes on the order is drawn
     * from this image, so a smaller one than the canvas has pixels for would
     * show up on the engraving, not just on screen.
     */
    snapImageWidth(width) {
        const steps = this.config.imageWidthSteps;
        return steps.find((step) => step >= width) || steps[steps.length - 1];
    }

    setImageLoading(isLoading) {
        let overlay = this.canvasContainer.querySelector('[data-image-loading]');

        if (isLoading) {
            if (!overlay) {
                overlay = document.createElement('div');
                overlay.setAttribute('data-image-loading', '');
                overlay.className = 'absolute inset-0 z-10 flex items-center justify-center bg-white/60';
                overlay.innerHTML =
                    '<div class="w-7 h-7 border-2 border-black border-t-transparent rounded-full animate-spin"></div>';
                this.canvasContainer.appendChild(overlay);
            }
        } else {
            overlay?.remove();
        }
    }

    loadProductImage(src, thumbnailImg = null) {
        if (!this.canvas?.width) {
            debug.log('loadProductImage: skipping, canvas not visible');
            return;
        }

        const fullSrc = this.getOptimalImageUrl(src);

        if (this.productImage.src === fullSrc) return;

        this._targetSrc = fullSrc;
        this.setImageLoading(true);

        if (thumbnailImg?.complete && thumbnailImg.naturalWidth > 0) {
            debug.log('Using DOM thumbnail as preview:', { naturalWidth: thumbnailImg.naturalWidth });
            this.productImage = thumbnailImg;
            this.setImageLoading(false);
            this.render();
            this.setImageLoading(true);
        }

        const img = new Image();
        img.crossOrigin = 'anonymous';
        // The customer is looking at a spinner until this lands, so it goes to
        // the front. Measured on a product page mid-load, the same image took
        // 10.8s at default priority and under a second once the page had
        // settled: the wait was queueing behind the page's own 395 requests,
        // not the CDN. Opening the dialog early was the worst case, and early
        // is exactly when people open it.
        if ('fetchPriority' in img) img.fetchPriority = 'high';
        img.onload = () => {
            if (this._targetSrc !== fullSrc) return;
            debug.log('Image loaded (full res):', { naturalWidth: img.naturalWidth });
            this.productImage = img;
            this.setImageLoading(false);
            this.syncFontSizePx();
            this.updateSizeSlider();
            // The default spot is a fraction of the photo, which is only known
            // once the photo is here.
            if (this._resetPositionOnLoad) this.resetTextPosition();
            this.render();
        };
        img.onerror = () => {
            debug.error('Image failed to load:', { src: fullSrc });
            this.setImageLoading(false);
        };
        img.src = fullSrc;
    }

    addEventListeners() {
        this.canvas.addEventListener('mousedown', this.handleMouseDown);
        this.canvas.addEventListener('mousemove', this.handleMouseMove);
        this.canvas.addEventListener('mouseup', this.handleMouseUp);
        this.canvas.addEventListener('mouseleave', this.handleMouseLeave);

        this.canvas.addEventListener('touchstart', this.handleTouchStart, { passive: false });
        this.canvas.addEventListener('touchmove', this.handleTouchMove, { passive: false });
        this.canvas.addEventListener('touchend', this.handleTouchEnd);

        this.fontInputs.forEach((input) => input.addEventListener('change', this.handleFontChange));

        this.textInput?.addEventListener('input', this.handleTextChange);
        this.sizeSlider?.addEventListener('input', this.handleSizeChange);
        this.rotationInputs.forEach((input) => input.addEventListener('change', this.handleRotationChange));
        this.orientationInputs.forEach((input) => input.addEventListener('change', this.handleStackChange));
        this.symbolPicker?.addEventListener('click', this.handleSymbolClick);
        this.colorInputs.forEach((input) => input.addEventListener('change', this.handleColorChange));
        this.form?.addEventListener('submit', this.handleApply);
        this.positionCheckConfirm?.addEventListener('click', this.handlePositionConfirm);
        this.positionCheckMove?.addEventListener('click', this.handlePositionMove);

        document.addEventListener('theme:resize', this.handleResize);
        document.addEventListener('product:variant-change', this.handleVariantChange);
        this.addEventListener('change', this.handleLocationChange);
        this.addEventListener('reload-on-event:loaded', this.handleReloadOnEvent);
        document.addEventListener('personalization:remove', this.handleRemoveRequest);
        // Capture, so these run before the theme's own add-to-cart handling.
        document.addEventListener('click', this.handleAddToCartGuard, true);
        document.addEventListener('submit', this.handleProductFormSubmitGuard, true);

        this.logoUploadBtn?.addEventListener('click', () => this.openLogoUploader());
        this.logoRemoveBtn?.addEventListener('click', () => this.removeLogo());
        this.logoSizeSlider?.addEventListener('input', (e) => {
            this.logoScale = parseInt(e.target.value) / 100;
            this.render();
        });
        this.logoFileInput?.addEventListener('change', this.handleLogoUpload);
        this.fontSuggestionBtn?.addEventListener('click', this.handleFontSuggestionClick);
        this.sizeHintBtn?.addEventListener('click', this.handleSizeHintToggle);
        this.sizeHintClose?.addEventListener('click', () => this.hideSizeHint());
        document.addEventListener('click', this.handleSizeHintOutsideClick);

        const modal = this.closest('modal-dialog');
        if (modal) {
            this.modalObserver = new MutationObserver(() => {
                if (modal.hasAttribute('open')) {
                    this.resizeCanvas();
                    this.initLocationSelector();
                    // After the dialog is laid out, so the scroller has a height.
                    requestAnimationFrame(() => this.bindControlsScroll());
                }
            });
            this.modalObserver.observe(modal, { attributes: true, attributeFilter: ['open'] });
        }

        this.resizeObserver = new ResizeObserver(() => {
            if (this.canvas?.width > 0) this.resizeCanvas();
        });
        this.resizeObserver.observe(this.canvasContainer);
    }

    cleanup() {
        if (this.canvas) {
            this.canvas.removeEventListener('mousedown', this.handleMouseDown);
            this.canvas.removeEventListener('mousemove', this.handleMouseMove);
            this.canvas.removeEventListener('mouseup', this.handleMouseUp);
            this.canvas.removeEventListener('mouseleave', this.handleMouseLeave);
            this.canvas.removeEventListener('touchstart', this.handleTouchStart);
            this.canvas.removeEventListener('touchmove', this.handleTouchMove);
            this.canvas.removeEventListener('touchend', this.handleTouchEnd);
        }

        if (this.moveHandle) {
            const btn = this.moveHandle;
            btn.removeEventListener('pointerdown', this.handleHandlePointerDown);
            btn.removeEventListener('pointermove', this.handleHandlePointerMove);
            btn.removeEventListener('pointerup', this.handleHandlePointerUp);
            btn.removeEventListener('pointercancel', this.handleHandlePointerUp);
            btn.removeEventListener('pointerenter', this.handleHandleEnter);
            btn.removeEventListener('pointerleave', this.handleHandleLeave);
        }
        if (this._hintRaf) cancelAnimationFrame(this._hintRaf);
        clearTimeout(this._hintLingerTimer);

        this.fontInputs.forEach((input) => input.removeEventListener('change', this.handleFontChange));

        this.textInput?.removeEventListener('input', this.handleTextChange);
        this.sizeSlider?.removeEventListener('input', this.handleSizeChange);
        this.rotationInputs.forEach((input) =>
            input.removeEventListener('change', this.handleRotationChange)
        );
        this.orientationInputs.forEach((input) =>
            input.removeEventListener('change', this.handleStackChange)
        );
        this.symbolPicker?.removeEventListener('click', this.handleSymbolClick);
        this.colorInputs.forEach((input) => input.removeEventListener('change', this.handleColorChange));
        this.form?.removeEventListener('submit', this.handleApply);
        this.positionCheckConfirm?.removeEventListener('click', this.handlePositionConfirm);
        this.positionCheckMove?.removeEventListener('click', this.handlePositionMove);

        document.removeEventListener('theme:resize', this.handleResize);
        document.removeEventListener('product:variant-change', this.handleVariantChange);
        this.removeEventListener('change', this.handleLocationChange);
        this.removeEventListener('reload-on-event:loaded', this.handleReloadOnEvent);
        document.removeEventListener('personalization:remove', this.handleRemoveRequest);
        document.removeEventListener('click', this.handleAddToCartGuard, true);
        document.removeEventListener('click', this.handleSizeHintOutsideClick);
        this._controlsScroller?.removeEventListener('scroll', this.handleControlsScroll);
        document.removeEventListener('submit', this.handleProductFormSubmitGuard, true);

        this.logoFileInput?.removeEventListener('change', this.handleLogoUpload);
        this.fontSuggestionBtn?.removeEventListener('click', this.handleFontSuggestionClick);

        clearTimeout(this._charsetWarningTimer);
        this.resizeObserver?.disconnect();
        this.modalObserver?.disconnect();

        this._targetSrc = null;
    }

    // ─── Drag helpers ────────────────────────────────────────────────────────

    /** Convert client coords to canvas-local coords */
    getCanvasPoint(clientX, clientY) {
        const rect = this.canvas.getBoundingClientRect();
        return { x: clientX - rect.left, y: clientY - rect.top };
    }

    /** Returns which draggable element is at the given canvas point, or null */
    getElementAtPoint(x, y) {
        // The press lands where the admin put it; nothing on a monogram moves.
        if (this.isMonogram()) return null;
        if (this.isPointOnLogo(x, y)) return 'logo';
        if (this.isPointOnText(x, y)) return 'text';
        return null;
    }

    startDrag(target, x, y) {
        const rect = this.canvas.getBoundingClientRect();
        const pos = target === 'text' ? this.textPosition : this.logoPosition;
        this.dragTarget = target;
        this.dragOffset = {
            x: x - (pos.x / 100) * rect.width,
            y: y - (pos.y / 100) * rect.height
        };
        if (target === 'text') {
            // Taking hold once is the proof the customer knows it moves. From
            // here the frame only shows on hover.
            this._hintPinned = false;
            this.hidePositionCheck();
            clearTimeout(this._hintLingerTimer);
            this.setHint(true);
            // A drag that starts on the thumbnail must not be undone when the
            // full-size photo arrives a moment later.
            this._resetPositionOnLoad = false;
        }
    }

    // ─── Drag hint ───────────────────────────────────────────────────────────

    /** Fade the dashed frame in or out. No-op in monogram mode. */
    setHint(visible) {
        if (this.isMonogram()) return;
        const target = visible ? 1 : 0;
        if (this._hintTarget === target) return;
        this._hintTarget = target;
        if (!this._hintRaf) {
            this._hintLastTs = 0;
            this._hintRaf = requestAnimationFrame(this.animateHint);
        }
    }

    /** Keep the frame up a moment after a finger lifts, then let it go. */
    lingerHint() {
        clearTimeout(this._hintLingerTimer);
        this._hintLingerTimer = setTimeout(() => {
            if (!this.dragTarget && !this._hintPinned) this.setHint(false);
        }, this.config.hint.touchLingerMs);
    }

    animateHint(ts) {
        const dt = this._hintLastTs ? ts - this._hintLastTs : 16;
        this._hintLastTs = ts;
        const step = dt / this.config.hint.fadeMs;
        const before = this._hintAlpha;
        this._hintAlpha =
            this._hintTarget > before ? Math.min(1, before + step) : Math.max(0, before - step);
        this.render();
        if (this._hintAlpha !== this._hintTarget) {
            this._hintRaf = requestAnimationFrame(this.animateHint);
        } else {
            this._hintRaf = null;
        }
    }

    /**
     * A dashed frame round the text, dark line under a light dash so it reads
     * on any leather. Drawn last, never into the mockup.
     */
    drawHint(alpha) {
        const rect = this.canvas.getBoundingClientRect();
        const { w, h } = this.getTextDisplaySize();
        const p = this.config.hint.padding;
        const x = Math.round((this.textPosition.x / 100) * rect.width - w / 2 - p) + 0.5;
        const y = Math.round((this.textPosition.y / 100) * rect.height - h / 2 - p) + 0.5;
        const bw = Math.round(w + p * 2);
        const bh = Math.round(h + p * 2);

        this.ctx.save();
        this.ctx.globalAlpha = alpha;
        this.ctx.lineWidth = 1;
        this.ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)';
        this.ctx.strokeRect(x, y, bw, bh);
        this.ctx.setLineDash([5, 4]);
        this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
        this.ctx.strokeRect(x, y, bw, bh);
        this.ctx.restore();
    }

    /**
     * Put the text back where a fresh engraving starts on this photo. Waits
     * for the photo when it has not arrived, since the spot is a fraction of
     * the drawn image and not of the canvas.
     */
    resetTextPosition() {
        const rect = this.canvas?.getBoundingClientRect();
        const ready = this.productImage.complete && this.productImage.naturalWidth > 0 && rect?.width && rect?.height;
        if (!ready) {
            this.textPosition = { ...this.config.defaultTextPosition };
            this._resetPositionOnLoad = true;
            return;
        }
        this._resetPositionOnLoad = false;
        const { dx, dy, drawWidth, drawHeight } = this.getImageDrawRect(rect);
        const def = this.config.defaultTextImagePosition;
        this.textPosition = {
            x: ((dx + (def.x / 100) * drawWidth) / rect.width) * 100,
            y: ((dy + (def.y / 100) * drawHeight) / rect.height) * 100
        };
    }

    moveDrag(x, y) {
        const rect = this.canvas.getBoundingClientRect();
        const pos = this.dragTarget === 'text' ? this.textPosition : this.logoPosition;
        pos.x = Math.max(0, Math.min(100, ((x - this.dragOffset.x) / rect.width) * 100));
        pos.y = Math.max(0, Math.min(100, ((y - this.dragOffset.y) / rect.height) * 100));
        this.render();
    }

    endDrag() {
        if (this.dragTarget) {
            this.dragTarget = null;
            this.canvas.style.cursor = 'grab';
        }
    }

    // ─── Mouse / Touch handlers ───────────────────────────────────────────────

    handleMouseDown(e) {
        const { x, y } = this.getCanvasPoint(e.clientX, e.clientY);
        const target = this.getElementAtPoint(x, y);
        if (target) {
            this.startDrag(target, x, y);
            this.canvas.style.cursor = 'grabbing';
        }
    }

    handleMouseMove(e) {
        const { x, y } = this.getCanvasPoint(e.clientX, e.clientY);
        if (this.dragTarget) {
            this.moveDrag(x, y);
            return;
        }
        const over = this.getElementAtPoint(x, y);
        this.canvas.style.cursor = over ? 'grab' : 'default';
        if (!this._hintPinned) this.setHint(over === 'text' || this._hintOverHandle);
    }

    handleMouseUp() {
        this.endDrag();
    }

    handleMouseLeave() {
        this.endDrag();
        if (!this._hintPinned && !this._hintOverHandle) this.setHint(false);
    }

    handleTouchStart(e) {
        // Let the dialog scroll under a finger when there is nothing to drag.
        if (this.isMonogram()) return;
        e.preventDefault();
        const touch = e.touches[0];
        const { x, y } = this.getCanvasPoint(touch.clientX, touch.clientY);
        const target = this.getElementAtPoint(x, y);
        if (target) this.startDrag(target, x, y);
    }

    handleTouchMove(e) {
        if (!this.dragTarget) return;
        e.preventDefault();
        const touch = e.touches[0];
        const { x, y } = this.getCanvasPoint(touch.clientX, touch.clientY);
        this.moveDrag(x, y);
    }

    handleTouchEnd() {
        const wasText = this.dragTarget === 'text';
        this.endDrag();
        if (wasText) this.lingerHint();
    }

    // ─── Hit testing ─────────────────────────────────────────────────────────

    isPointOnText(x, y) {
        if (!this.textInput?.value) return false;

        const rect = this.canvas.getBoundingClientRect();
        const { w, h } = this.getTextDisplaySize();

        return this.isPointInBounds(x, y, {
            cx: (this.textPosition.x / 100) * rect.width,
            cy: (this.textPosition.y / 100) * rect.height,
            w,
            h
        });
    }

    /**
     * Axis-aligned canvas footprint of the engraving. Rotating 90 or 270
     * swaps the block's width and height; stacking turns it into a column
     * as tall as the character count.
     */
    getTextDisplaySize() {
        const text = this.textInput?.value || '';
        const fontSize = this.getFontSize();
        const font = this.getSelectedFont();

        if (this.isStacked()) {
            const chars = [...text];
            const widest = chars.reduce(
                (max, char) => Math.max(max, this.measureTextWidth(char, fontSize, font)),
                0
            );
            const run =
                Math.max(0, chars.length - 1) * fontSize * this.config.stackLineHeightRatio + fontSize;
            return { w: widest, h: run };
        }

        const width = this.measureTextWidth(text, fontSize, font);
        return this.textRotation % 180 === 0 ? { w: width, h: fontSize } : { w: fontSize, h: width };
    }

    isPointOnLogo(x, y) {
        if (!this.logoImage?.complete || !this.logoImage?.naturalWidth) return false;

        const rect = this.canvas.getBoundingClientRect();
        const { w, h } = this.getLogoDisplaySize(rect);

        return this.isPointInBounds(x, y, {
            cx: (this.logoPosition.x / 100) * rect.width,
            cy: (this.logoPosition.y / 100) * rect.height,
            w,
            h
        });
    }

    /** Shared bounds check, cx/cy are element centres, w/h are full dimensions */
    isPointInBounds(x, y, { cx, cy, w, h }) {
        const p = this.config.hitAreaPadding;
        return x >= cx - w / 2 - p && x <= cx + w / 2 + p && y >= cy - h / 2 - p && y <= cy + h / 2 + p;
    }

    // ─── Font / size helpers ──────────────────────────────────────────────────

    handleReloadOnEvent() {
        this.initLocationSelector();
    }

    handleFontChange() {
        this._measureCache.clear();
        const newFont = this.getSelectedFont();
        const text = this.textInput?.value || '';

        debug.log('Font changed:', { from: this.previousFont, to: newFont });

        this.prewarmSelectedFontForDXF();

        if (text.length > 0 && this.previousFont && this.previousFont !== newFont) {
            const currentWidth = this.measureTextWidth(text, this.currentFontSize, this.previousFont);
            this.setFontSizeMm(this.pxToMm(this.findFontSizeForWidth(text, newFont, currentWidth)));
        }

        this.previousFont = newFont;
        // Switching font can invalidate text that was fine a moment ago, so the
        // coverage check has to run again against the new choice.
        this.updateFontSuggestion(text);
        this.updateSizeSlider();
        this.updateCharacterCounter();

        const maxChars = this.calculateMaxCharacters(this.currentFontSize);
        if (this.textInput && this.textInput.value.length > maxChars) {
            this.textInput.value = this.textInput.value.substring(0, maxChars);
        }

        this.render();
    }

    handleTextChange(e) {
        if (this.isMonogram()) this.applyMonogramGate(e.target);
        else this.applyCharsetGate(e.target);

        const text = e.target.value;
        const maxChars = this.calculateMaxCharacters(this.currentFontSize);
        const overLimit = text.length > maxChars;
        if (overLimit) e.target.value = text.substring(0, maxChars);
        this.updateCharacterCounter();
        // Only on the keystroke that was refused, and only while there is a
        // smaller size left to drop to, since otherwise the advice is useless.
        if (overLimit) this.maybeOfferSmallerSize(e.target);
        else if (e.target.value.length < maxChars) this.hideSizeHint();
        // The frame comes up with the first character and goes with the last,
        // until the customer has moved the text once.
        if (this._hintPinned) this.setHint(e.target.value.length > 0);
        this.render();
    }

    /**
     * The type case holds capitals, digits and a period, nothing else. Input is
     * uppercased first so a lowercase letter becomes the capital that will be
     * pressed rather than being refused; everything else is dropped and named,
     * with the caret kept where the customer left it.
     *
     * @param {HTMLInputElement} input
     */
    applyMonogramGate(input) {
        const before = input.value;
        const caret = input.selectionStart ?? before.length;
        const gate = (s) => {
            const kept = [];
            const removed = [];
            const nfc = typeof s.normalize === 'function' ? s.normalize('NFC') : s;
            for (const ch of nfc.toUpperCase()) {
                if (this.config.monogram.allowedChar.test(ch)) kept.push(ch);
                else if (!/\s/.test(ch) && !removed.includes(ch)) removed.push(ch);
            }
            return { text: kept.join(''), removed };
        };
        const { text, removed } = gate(before);

        if (text !== before) {
            const droppedBeforeCaret = before.slice(0, caret).length - gate(before.slice(0, caret)).text.length;
            input.value = text;
            const next = Math.max(0, caret - droppedBeforeCaret);
            input.setSelectionRange?.(next, next);
        }

        if (removed.length) {
            this.showCharsetWarning(
                this.formatMessage(this.dataset.errorCharNotEngravable, { chars: removed.join(' ') })
            );
        }
    }

    handleColorChange() {
        debug.log('Monogram color changed:', { color: this.getSelectedColorKey() });
        this.render();
    }

    /** Machine key of the chosen foil: transparent, gold, silver or black. */
    getSelectedColorKey() {
        return this.querySelector('[data-color-select]:checked')?.dataset.color || 'transparent';
    }

    /** The chosen foil as the customer will read it on the order. */
    getSelectedColorLabel() {
        return this.querySelector('[data-color-select]:checked')?.value || '';
    }

    /**
     * Strip anything that could never be engraved and say what went, then check
     * what is left against the chosen font. Runs on typing and on paste, because
     * both land as an `input` event.
     *
     * @param {HTMLInputElement} input
     */
    applyCharsetGate(input) {
        const before = input.value;
        const caret = input.selectionStart ?? before.length;
        const { text, removed } = filterStructural(before);

        if (text !== before) {
            // Keep the caret where the customer left it, minus whatever was
            // dropped ahead of it, so typing does not jump to the end.
            const droppedBeforeCaret =
                before.slice(0, caret).length - filterStructural(before.slice(0, caret)).text.length;
            input.value = text;
            const next = Math.max(0, caret - droppedBeforeCaret);
            input.setSelectionRange?.(next, next);
        }

        if (removed.length) {
            this.showCharsetWarning(
                this.formatMessage(this.dataset.errorCharNotEngravable, { chars: removed.join(' ') })
            );
        }

        this.updateFontSuggestion(text);
    }

    /**
     * Everything the structural gate let through still has to exist in a font.
     * A character no font has is refused outright. A letter the chosen font lacks
     * but another font has becomes an offer to switch, which keeps the sale and
     * keeps the lettering in one typeface.
     *
     * @param {string} text
     */
    updateFontSuggestion(text) {
        if (!this.fontSuggestion) return;

        const entries = this.getLoadedFontEntries();
        if (!entries.length) {
            this.fontSuggestion.classList.add('hidden');
            return;
        }

        const selected = this.getSelectedFont();
        const primary = entries.find((entry) => entry.name === selected);
        if (!primary) {
            this.fontSuggestion.classList.add('hidden');
            return;
        }

        const { unsupported, elsewhere } = classifyAgainstFonts(text, primary, entries);

        if (unsupported.length) {
            const input = this.textInput;
            if (input) {
                const cleaned = [...input.value].filter((ch) => !unsupported.includes(ch)).join('');
                if (cleaned !== input.value) input.value = cleaned;
            }
            this.showCharsetWarning(
                this.formatMessage(this.dataset.errorCharNoFont, { chars: unsupported.join(' ') })
            );
            this.render();
        }

        if (!elsewhere.length) {
            this.fontSuggestion.classList.add('hidden');
            this._suggestedFont = null;
            return;
        }

        // Offer the font that covers the most of what is missing.
        const tally = new Map();
        for (const item of elsewhere) {
            for (const name of item.fonts) tally.set(name, (tally.get(name) || 0) + 1);
        }
        const best = [...tally.entries()].sort((a, b) => b[1] - a[1])[0][0];
        const chars = elsewhere.map((item) => item.char).join(' ');

        this._suggestedFont = best;
        if (this.fontSuggestionText) {
            this.fontSuggestionText.textContent = this.formatMessage(this.dataset.fontSuggestion, {
                chars,
                font: selected
            });
        }
        if (this.fontSuggestionName) this.fontSuggestionName.textContent = best;
        this.fontSuggestion.classList.remove('hidden');
    }

    handleFontSuggestionClick(e) {
        e.preventDefault();
        this.acceptFontSuggestion();
    }

    /** Swap to the font the suggestion named, then re-run the check. */
    acceptFontSuggestion() {
        if (!this._suggestedFont) return;
        const input = this.querySelector(`input[name="font"][value="${CSS.escape(this._suggestedFont)}"]`);
        if (!input) return;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /** Every engraving font that has finished loading, primary order irrelevant. */
    getLoadedFontEntries() {
        if (!this._fontEntries) return [];
        return Array.from(this.fontInputs)
            .map((input) => ({ name: input.value, font: this._fontEntries.get(input.dataset.fontUrl) }))
            .filter((entry) => entry.font);
    }

    /** Tiny {{ placeholder }} substitution, matching the Liquid strings. */
    formatMessage(template, values) {
        if (!template) return '';
        return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => values[key] ?? '');
    }

    handleSymbolClick(e) {
        const btn = e.target.closest('[data-symbol]');
        if (!btn || !this.textInput) return;

        const symbol = btn.dataset.symbol;
        const maxChars = this.calculateMaxCharacters(this.currentFontSize);

        if (this.textInput.value.length + symbol.length > maxChars) {
            this.maybeOfferSmallerSize(this.textInput);
            this.showWarning(this.dataset.errorCharLimit || 'Character limit reached for this text size');
            return;
        }

        const start = this.textInput.selectionStart ?? this.textInput.value.length;
        const end = this.textInput.selectionEnd ?? this.textInput.value.length;
        const before = this.textInput.value.slice(0, start);
        const after = this.textInput.value.slice(end);
        this.textInput.value = before + symbol + after;
        this.textInput.focus();
        this.textInput.selectionStart = this.textInput.selectionEnd = start + symbol.length;
        this.textInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    handleSizeChange(e) {
        const requestedMm = parseFloat(e.target.value);
        const requestedSize = this.mmToPx(requestedMm);
        const textLength = this.textInput?.value.length || 0;
        const maxChars = this.calculateMaxCharacters(requestedSize);

        if (requestedSize > this.currentFontSize && textLength > maxChars) {
            e.target.value = this.currentFontSizeMm;
            this.showWarning(
                this.dataset.errorTextTooLongForSize ||
                    'Reduce the message length before increasing the text size.'
            );
            return;
        }

        this.setFontSizeMm(requestedMm);
        this.updateCharacterCounter();
        // The advice has been taken, so the note goes rather than sitting over
        // the controls while the customer carries on.
        this.hideSizeHint();
        this.render();
    }

    // ─── Rotation ─────────────────────────────────────────────────────────────

    handleRotationChange(e) {
        this.textRotation = this.normalizeRotation(e.target.value);
        this.updateStackVisibility();
        this.applyLayoutConstraints();

        debug.log('Rotation changed:', { rotation: this.textRotation, stacked: this.isStacked() });

        this.render();
    }

    handleStackChange(e) {
        this.stackLetters = e.target.value === 'stacked';
        this.applyLayoutConstraints();

        debug.log('Stack letters changed:', { stacked: this.isStacked() });

        this.render();
    }

    /** Snap any incoming value to one of 0 / 90 / 180 / 270 */
    normalizeRotation(value) {
        const degrees = Math.round(parseInt(value, 10) / 90) * 90;
        return Number.isFinite(degrees) ? ((degrees % 360) + 360) % 360 : 0;
    }

    /**
     * True when the message runs vertically with every glyph left upright,
     * so it reads top to bottom instead of asking the reader to tilt their head.
     */
    isStacked() {
        return this.stackLetters && this.textRotation % 180 !== 0;
    }

    /**
     * The letter-direction picker only does anything on a vertical run, and its
     * two samples are kept in step with the chosen angle: the turned sample
     * leans the same way, and the stacked one reads bottom to top at 270 just
     * as the engraving will.
     */
    updateStackVisibility() {
        const vertical = this.textRotation % 180 !== 0;

        this.stackWrapper?.classList.toggle('hidden', !vertical);
        this.stackWrapper?.style.setProperty('--engraving-rotation', `${this.textRotation}deg`);

        if (this.stackSample) {
            this.stackSample.style.flexDirection = this.textRotation === 270 ? 'column-reverse' : 'column';
        }
    }

    /** Unit vector the message advances along, in canvas space */
    getAdvanceVector() {
        const radians = (this.textRotation * Math.PI) / 180;
        return { x: Math.cos(radians), y: Math.sin(radians) };
    }

    /**
     * Stacking changes how much of the engraving run each character eats, so
     * re-derive the size cap and trim anything that no longer fits.
     */
    applyLayoutConstraints() {
        this.updateSizeSlider();

        const maxChars = this.calculateMaxCharacters(this.currentFontSize);
        if (this.textInput && this.textInput.value.length > maxChars) {
            this.textInput.value = this.textInput.value.substring(0, maxChars);
        }

        this.updateCharacterCounter();
    }

    // ─── Logo upload ──────────────────────────────────────────────────────────

    openLogoUploader() {
        if (!this.logoFileInput) {
            debug.error('openLogoUploader: file input not available');
            return;
        }
        this.logoFileInput.click();
    }

    async handleLogoUpload(e) {
        const file = e.target?.files?.[0];
        if (!file) {
            debug.warn('handleLogoUpload: no file selected');
            return;
        }

        debug.log('handleLogoUpload: file selected', {
            name: file.name,
            type: file.type,
            size: file.size
        });

        if (file.type !== 'image/svg+xml') {
            this.showWarning(this.dataset.uploadErrorText || 'Logo must be an SVG file.');
            e.target.value = '';
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            this.showWarning(this.dataset.uploadErrorText || 'Logo file is too large (max 5 MB).');
            e.target.value = '';
            return;
        }

        let url;
        try {
            url = await this.uploadToWorker(file, this.buildUploadFilename('svg'));
        } catch (err) {
            debug.error('handleLogoUpload: upload failed', { err });
            this.showWarning(this.dataset.uploadErrorText || 'Could not upload logo, please try again.');
            e.target.value = '';
            return;
        }

        debug.log('handleLogoUpload: uploaded, loading image from', { url });
        this.logoImageUrl = url;
        this.logoImage = new Image();
        this.logoImage.crossOrigin = 'anonymous';
        this.logoImage.onload = () => {
            debug.log('handleLogoUpload: image loaded', {
                w: this.logoImage.naturalWidth,
                h: this.logoImage.naturalHeight
            });
            if (this.logoUploadBtn)
                this.logoUploadBtn.textContent = this.dataset.changeLogoText || 'Change logo';
            if (this.logoRemoveBtn) this.logoRemoveBtn.style.display = '';
            const sizeWrapper = this.logoSizeSlider?.closest('[data-logo-size-wrapper]');
            if (sizeWrapper) sizeWrapper.style.display = '';
            this.render();
        };
        this.logoImage.onerror = () => {
            debug.error('handleLogoUpload: failed to load image from URL', { url });
        };
        this.logoImage.src = url;

        // Reset so re-selecting the same file still fires `change`.
        e.target.value = '';
    }

    removeLogo() {
        debug.log('removeLogo: removing logo');
        this.logoImage = null;
        this.logoImageUrl = null;
        this.logoPosition = { ...this.config.defaultLogoPosition };
        this.logoScale = this.config.defaultLogoScale;
        if (this.logoSizeSlider) this.logoSizeSlider.value = this.config.defaultLogoScale * 100;
        if (this.logoUploadBtn) this.logoUploadBtn.textContent = this.dataset.uploadLogoText || 'Upload logo';
        if (this.logoRemoveBtn) this.logoRemoveBtn.style.display = 'none';
        const sizeWrapper = this.logoSizeSlider?.closest('[data-logo-size-wrapper]');
        if (sizeWrapper) sizeWrapper.style.display = 'none';
        this.render();
    }

    // ─── Logo sizing ──────────────────────────────────────────────────────────

    /**
     * Returns display { w, h } for the logo, clamped to maxLogoWidthCm × maxLogoHeightCm.
     * Uses the same cm→px logic as text constraints.
     */
    getLogoDisplaySize(rect) {
        const maxW = this.cmToPixels(this.config.maxLogoWidthCm, rect) * this.logoScale;
        const maxH = this.cmToPixels(this.config.maxLogoHeightCm, rect) * this.logoScale;
        const aspect = this.logoImage.naturalWidth / this.logoImage.naturalHeight;

        let w = Math.min(this.logoImage.naturalWidth, maxW);
        let h = w / aspect;

        if (h > maxH) {
            h = maxH;
            w = h * aspect;
        }

        return { w, h };
    }

    /**
     * Compute the "contain"-fitted draw rect for the product image inside the canvas.
     * Wide images fill the width (unchanged); tall images fill the height and get
     * centered horizontally, so nothing is clipped off the top/bottom.
     */
    getImageDrawRect(rect) {
        const imgAspect = this.productImage.naturalWidth / this.productImage.naturalHeight;
        const containerAspect = rect.width / rect.height;
        let drawWidth;
        let drawHeight;
        if (imgAspect >= containerAspect) {
            drawWidth = rect.width;
            drawHeight = drawWidth / imgAspect;
        } else {
            drawHeight = rect.height;
            drawWidth = drawHeight * imgAspect;
        }
        return {
            drawWidth,
            drawHeight,
            dx: (rect.width - drawWidth) / 2,
            dy: (rect.height - drawHeight) / 2
        };
    }

    /** Width in canvas pixels actually occupied by the product image */
    getDisplayedImageWidth(rect) {
        if (this.productImage.complete && this.productImage.naturalWidth > 0) {
            return this.getImageDrawRect(rect).drawWidth;
        }
        return rect.width;
    }

    /** Convert cm to canvas pixels, using image cm width when available */
    cmToPixels(cm, rect) {
        const pxPerCm = this.currentImageCm
            ? this.getDisplayedImageWidth(rect) / this.currentImageCm
            : this.config.pixelsPerCm;
        return pxPerCm * cm;
    }

    // ─── Apply personalization ────────────────────────────────────────────────

    async handleApply(e) {
        e.preventDefault();

        const selectedFont = this.querySelector('input[name="font"]:checked');
        const text = this.textInput?.value || '';

        if (!text.trim()) {
            this.showWarning(this.dataset.errorEnterText || 'Please enter engraving text');
            return;
        }

        const maxChars = this.calculateMaxCharacters(this.currentFontSize);
        if (text.length > maxChars) {
            this.showWarning(`Text exceeds maximum length of ${maxChars} characters for this size`);
            return;
        }

        // The engraving is still where it started, so the customer may not
        // know it was theirs to place. Ask once, then let the answer stand for
        // the rest of the session.
        if (this.shouldCheckPosition()) {
            this.showPositionCheck();
            return;
        }

        const workerUrl = this.dataset.uploadWorkerUrl;
        const specialNotes = this.specialNotesInput?.value.trim() || '';
        const monogram = this.isMonogram();

        debug.log('handleApply: inputs', {
            text,
            mode: monogram ? 'monogram' : 'engraving',
            selectedFontValue: selectedFont?.value,
            selectedFontUrl: selectedFont?.dataset.fontUrl,
            hasWorkerUrl: !!workerUrl,
            specialNotesLen: specialNotes.length,
            editLineKey: this.editLineKey || null
        });

        this.setApplyBusy(true);

        // Generate the DXF and mockup JPG in parallel; both upload to our worker.
        // _dxf_url is hidden (staff only); _mockup_url is hidden but read by the
        // cart template to render a preview thumbnail. A monogram is set by hand
        // from the letters on the order, so it has no cut file.
        let dxfUrl = null;
        let mockupUrl = null;
        let dxfError = null;
        try {
            [dxfUrl, mockupUrl] = await Promise.all([
                monogram
                    ? null
                    : this.uploadEngravingDXF(text, selectedFont).catch((err) => {
                          debug.error('handleApply: DXF upload failed', { err });
                          dxfError = err;
                          return null;
                      }),
                workerUrl
                    ? this.uploadCanvasMockup().catch((err) => {
                          debug.error('handleApply: mockup upload failed', { err });
                          return null;
                      })
                    : null
            ]);
        } catch (err) {
            debug.error('handleApply: upload batch failed', { err });
            dxfError = dxfError || err;
        }

        debug.log('handleApply: upload results', { dxfUrl, mockupUrl });

        // A missing DXF used to reach production as silence: the order looked
        // normal and the gap only showed up when someone went to cut it. The
        // sale still goes through, but it says so on the line item and it
        // reports itself, so it is caught the same day rather than at the laser.
        if (!dxfUrl && !monogram) this.reportMissingDXF(text, selectedFont, dxfError);

        const personalizationData = {
            message: text,
            font: selectedFont?.value || '',
            fontSize: this.currentFontSize,
            fontSizeMm: this.currentFontSizeMm,
            rotation: this.textRotation,
            stackLetters: this.isStacked(),
            location: this.querySelector('[data-location-select]:checked')?.value || '',
            position: { x: this.textPosition.x, y: this.textPosition.y }
        };

        if (monogram) {
            personalizationData.type = 'monogram';
            // American spelling on anything that crosses a boundary. This key
            // is read by a Shopify Flow action, and the British spelling here
            // against `color` there meant the monogram color arrived blank on
            // every order with nothing erroring.
            personalizationData.color = this.getSelectedColorLabel();
        }

        if (this.logoImageUrl) {
            personalizationData.logoUrl = this.logoImageUrl;
            personalizationData.logoPosition = { ...this.logoPosition };
        }

        if (specialNotes) {
            personalizationData.specialNotes = specialNotes;
        }

        // Fold upload URLs into _engraving so bundling/cart-transform that
        // strips other underscore-properties still preserves them.
        if (dxfUrl) personalizationData.dxfUrl = dxfUrl;
        if (mockupUrl) personalizationData.mockupUrl = mockupUrl;

        const properties = this.buildLineProperties(
            text,
            personalizationData,
            specialNotes,
            dxfUrl,
            mockupUrl,
            dxfError
        );
        debug.log('handleApply: final properties bag', {
            keys: Object.keys(properties),
            has_dxf_url: '_dxf_url' in properties,
            has_mockup_url: '_mockup_url' in properties,
            has_logo_url: '_logo_url' in properties
        });

        // Edit mode: update the existing cart line and redirect back to /cart.
        if (this.editLineKey) {
            try {
                await this.replaceCartLine(properties);
                window.location.href = '/cart';
            } catch (err) {
                debug.error('handleApply: cart update failed', { err });
                this.showWarning(this.dataset.uploadErrorText || 'Could not update cart, please try again.');
                this.setApplyBusy(false);
            }
            return;
        }

        // Normal mode: write properties into the product form so Add to Cart picks them up.
        const productForm = document.querySelector('[data-product-form]');
        if (!productForm) {
            debug.error('handleApply: [data-product-form] not found, cannot save personalization');
            this.setApplyBusy(false);
            return;
        }

        Object.entries(properties).forEach(([key, value]) => {
            this.addPropertyInput(productForm, `properties[${key}]`, value);
        });
        // Only ever take back the inputs we put there. Other apps write their own
        // line item properties onto the same form, and clearing by prefix would
        // strip Rebuy's attribution along with the engraving.
        this._appliedPropertyKeys = Object.keys(properties);
        // What is on the form now matches what is on screen, and the screen is
        // the variant the customer is looking at.
        this._appliedVariantId = String(
            document.querySelector('[data-variant-select]')?.value || this._appliedVariantId || ''
        );
        this._needsRevalidation = false;

        document.dispatchEvent(
            new CustomEvent('personalization:applied', {
                detail: { ...personalizationData, mockupUrl, dxfUrl }
            })
        );

        this.setApplyBusy(false);
        this.closeModal();
    }

    buildLineProperties(text, personalizationData, specialNotes, dxfUrl, mockupUrl, dxfError) {
        // The same hidden payload for both kinds, so the cart, the edit link and
        // the preboot prefill read one key. The visible name says which it is.
        const props = { _engraving: JSON.stringify(personalizationData) };
        // Front or Interior is the first thing whoever makes the piece needs
        // to know. It was only inside the hidden JSON and the mockup image, so
        // it gets its own visible line next to the text.
        const location = personalizationData.location || '';

        if (this.isMonogram()) {
            props.Monogram = text;
            props['Monogram Color'] = personalizationData.color || '';
            if (location) props['Monogram Location'] = location;
            if (specialNotes) props['Special Notes'] = specialNotes;
            if (mockupUrl) props._mockup_url = mockupUrl;
            return props;
        }

        props.Engraving = text;
        if (location) props['Engraving Location'] = location;
        if (specialNotes) props['Special Notes'] = specialNotes;
        if (this.logoImageUrl) props._logo_url = this.logoImageUrl;
        if (dxfUrl) props._dxf_url = dxfUrl;
        if (mockupUrl) props._mockup_url = mockupUrl;
        // Underscore-prefixed, so the customer never sees it, but it sits on the
        // order in the admin where whoever prepares the engraving will look.
        if (!dxfUrl) props._dxf_error = String(dxfError?.message || dxfError || 'DXF not generated');
        return props;
    }

    handleRemoveRequest() {
        this.clearPersonalization();
    }

    /**
     * Take the engraving back off the product form and reset the modal, so the
     * customer can decide against it without reloading or adding to the cart
     * first. Only reachable before the line exists; once it is in the cart the
     * cart's own remove handles it.
     */
    clearPersonalization() {
        const productForm = document.querySelector('[data-product-form]');
        const keys = this._appliedPropertyKeys || [
            'Engraving',
            'Engraving Location',
            'Monogram',
            'Monogram Color',
            'Monogram Location',
            '_engraving',
            'Special Notes',
            '_logo_url',
            '_dxf_url',
            '_mockup_url',
            '_dxf_error'
        ];

        if (productForm) {
            keys.forEach((key) => {
                const input = Array.from(productForm.elements).find((el) => el.name === `properties[${key}]`);
                input?.remove();
            });
        }
        this._appliedPropertyKeys = null;
        this._appliedVariantId = null;
        this._needsRevalidation = false;
        this._preserveTextPosition = false;

        // Reset the modal too, otherwise reopening it shows the engraving the
        // customer just removed and the next Apply silently restores it.
        if (this.textInput) this.textInput.value = '';
        if (this.specialNotesInput) this.specialNotesInput.value = '';
        this.resetTextPosition();
        this._hintPinned = true;
        this._positionConfirmed = false;
        this.hidePositionCheck();
        this.setHint(false);
        this.setFontSizeMm(
            this.config.defaultFontSizeMm
        );
        if (this.isMonogram()) this.applyMonogramSize();
        if (this.colorInputs[0]) this.colorInputs[0].checked = true;
        this.textRotation = this.config.defaultTextRotation;
        this.stackLetters = false;
        this.removeLogo();
        this.updateStackVisibility();
        this.updateSizeSlider();
        this.updateCharacterCounter();
        this.charsetWarning?.classList.add('hidden');
        this.fontSuggestion?.classList.add('hidden');
        this.hideSizeHint();
        this.render();

        debug.log('clearPersonalization: removed', { keys });
        document.dispatchEvent(new CustomEvent('personalization:removed'));
    }

    /**
     * @param {string} text
     * @param {HTMLInputElement|null} fontInput
     * @param {Error|null} err
     */
    reportMissingDXF(text, fontInput, err) {
        const detail = {
            message: 'engraving DXF missing, order will ship without a cut file',
            err: String(err?.message || err || 'no error thrown'),
            stack: err?.stack ? String(err.stack) : '',
            extra: {
                product: this.dataset.productHandle || '',
                font: fontInput?.value || '',
                // The unusual characters only, never the message. A name is the
                // customer's, and the letters have never been the problem: it is
                // the symbols the picker offers that some fonts cannot draw.
                symbols: [...new Set(text)]
                    .filter((ch) => !/[A-Za-z0-9 .,'&-]/.test(ch))
                    .sort()
                    .join(''),
                length: text.length,
                rotation: this.textRotation,
                stacked: this.isStacked(),
                location: this.querySelector('[data-location-select]:checked')?.value || '',
                editing: !!this.editLineKey
            }
        };

        debug.error('reportMissingDXF', detail);

        try {
            if (window.slog) window.slog('engraving_dxf_missing', detail);
        } catch (reportErr) {
            debug.warn('reportMissingDXF: reporting failed', { reportErr });
        }
    }

    /**
     * Replace the properties on the line being edited in a single request.
     * Per shopify.dev/docs/api/ajax/reference/cart, /cart/change.js with a
     * `properties` payload overwrites the entire properties bag, exactly
     * what we want since we rebuild the full bag every save.
     */
    async replaceCartLine(properties) {
        const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
                id: this.editLineKey,
                quantity: this.editQuantity,
                properties
            })
        });
        if (!res.ok) {
            const body = await res.text().catch(() => '');
            throw new Error(`/cart/change.js failed (${res.status}): ${body}`);
        }
    }

    /**
     * Build the engraving DXF and upload it to the worker.
     * @param {string} text
     * @param {HTMLInputElement|null} fontInput
     * @returns {Promise<string|null>} Public URL of the uploaded DXF, or null on failure.
     */
    async uploadEngravingDXF(text, fontInput) {
        debug.log('uploadEngravingDXF: starting', {
            text,
            fontValue: fontInput?.value,
            fontUrl: fontInput?.dataset.fontUrl
        });

        const fontUrl = fontInput?.dataset.fontUrl;
        if (!fontUrl) {
            debug.warn('uploadEngravingDXF: ABORT, no data-font-url on selected font input');
            return null;
        }

        if (!this.dataset.uploadWorkerUrl) {
            debug.warn('uploadEngravingDXF: ABORT, missing data-upload-worker-url on <personalize-product>');
            return null;
        }

        // Measure the canvas synchronously, closeModal() runs while we're awaiting
        // the font/dxf modules, so by the time async work resumes the canvas is hidden.
        const canvasRect = this.canvas?.getBoundingClientRect();
        const imageDrawWidth = canvasRect ? this.getDisplayedImageWidth(canvasRect) : 0;
        const pxPerCm =
            this.currentImageCm && imageDrawWidth
                ? imageDrawWidth / this.currentImageCm
                : this.config.pixelsPerCm;
        // The size is held in mm, so the file no longer depends on how wide
        // the canvas happened to be on the device that placed the order.
        const fontSizeMm = this.currentFontSizeMm;

        debug.log('uploadEngravingDXF: measurements', {
            currentFontSize: this.currentFontSize,
            currentImageCm: this.currentImageCm,
            imageDrawWidth,
            pxPerCm,
            fontSizeMm,
            rotation: this.textRotation,
            stacked: this.isStacked()
        });

        debug.log('uploadEngravingDXF: loading DXF modules + font');
        const font = await this.resolveFontForText(text, fontUrl);
        const { generateEngravingDXFString } = await loadDxfModules();
        debug.log('uploadEngravingDXF: font ready', { hasFont: !!font });

        const dxfText = generateEngravingDXFString(text, {
            font,
            fontSizeMm,
            rotationDeg: this.textRotation,
            stacked: this.isStacked(),
            stackLineHeightRatio: this.config.stackLineHeightRatio
        });
        debug.log('uploadEngravingDXF: dxf generated', { bytes: dxfText.length });

        // DXF is plain ASCII; worker MIME allowlist accepts text/plain.
        const blob = new Blob([dxfText], { type: 'text/plain' });
        const filename = this.buildUploadFilename('dxf');
        debug.log('uploadEngravingDXF: posting to worker', { filename, blobSize: blob.size });

        const url = await this.uploadToWorker(blob, filename);
        debug.log('uploadEngravingDXF: SUCCESS', { url });
        return url;
    }

    /**
     * The chosen font, backed by the other engraving fonts for any character it
     * doesn't carry. Only the selected font is fetched in the common case; the
     * others are pulled in when the message actually needs them, which today
     * means a symbol from the picker on a font that has no symbols.
     *
     * @param {string} text
     * @param {string} fontUrl Selected font's .font.json URL.
     * @returns {Promise<Object>} An opentype-compatible font covering `text`.
     */
    async resolveFontForText(text, fontUrl) {
        const { loadOpentypeFont, getResolvedFont, createCompositeFont } = await loadDxfModules();
        const primary = getResolvedFont(fontUrl) || (await loadOpentypeFont(fontUrl));

        const missing = [...text].filter((ch) => !/\s/.test(ch) && !primary.hasChar(ch));
        if (!missing.length) return primary;

        const fallbackUrls = Array.from(this.fontInputs)
            .map((input) => input.dataset.fontUrl)
            .filter((url) => url && url !== fontUrl);

        debug.warn('resolveFontForText: selected font is missing glyphs, loading fallbacks', {
            missing: [...new Set(missing)],
            fallbackUrls
        });

        const fallbacks = await Promise.all(
            fallbackUrls.map((url) =>
                (getResolvedFont(url) ? Promise.resolve(getResolvedFont(url)) : loadOpentypeFont(url)).catch(
                    (err) => {
                        debug.warn('resolveFontForText: fallback failed to load', { url, err });
                        return null;
                    }
                )
            )
        );

        return createCompositeFont([primary, ...fallbacks.filter(Boolean)]);
    }

    addPropertyInput(form, name, value) {
        const existing = Array.from(form.elements).find((el) => el.name === name);

        if (existing) {
            existing.value = value;
            return;
        }

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
    }

    // ─── Upload helpers ───────────────────────────────────────────────────────

    buildUploadFilename(ext) {
        const storeName = this.dataset.storeName || 'store';
        const handle = this.dataset.productHandle || 'product';
        const tzOffset = this.dataset.storeTzOffset || '+0000';
        const sign = tzOffset[0] === '-' ? -1 : 1;
        const offsetMins = sign * (parseInt(tzOffset.slice(1, 3)) * 60 + parseInt(tzOffset.slice(3, 5)));
        const local = new Date(Date.now() + offsetMins * 60000);
        const datetime = local.toISOString().slice(0, 19).replace('T', '_').replace(/:/g, '-');
        return `${storeName}-${handle}-${datetime}.${ext}`;
    }

    async uploadToWorker(blob, filename) {
        const workerUrl = this.dataset.uploadWorkerUrl;
        if (!workerUrl) {
            throw new Error('uploadToWorker: missing data-upload-worker-url');
        }

        const formData = new FormData();
        formData.append('file', blob, filename);

        const response = await fetch(`${workerUrl.replace(/\/$/, '')}/upload`, {
            method: 'POST',
            body: formData
        });

        if (!response.ok) {
            const body = await response.text().catch(() => '');
            throw new Error(`Upload worker ${response.status}: ${body}`);
        }

        const data = await response.json();
        if (!data?.url) {
            throw new Error('Upload worker returned no url');
        }
        return data.url;
    }

    // ─── Canvas upload ────────────────────────────────────────────────────────

    // async, so a synchronous canvas failure becomes a rejection this method's own
    // caller can catch. Drawing a zero-sized canvas throws on the spot, and that
    // throw used to escape the Promise.all in handleApply, taking a DXF that had
    // generated perfectly well down with it and then filing it under _dxf_error.
    async uploadCanvasMockup() {
        const { uploadMaxSize, uploadJpegQuality } = this.config;
        const src = this.canvas;
        if (!src?.width || !src?.height) {
            throw new Error(`uploadCanvasMockup: canvas is ${src?.width}x${src?.height}`);
        }
        const scale = Math.min(1, uploadMaxSize / Math.max(src.width, src.height));
        const offscreen = document.createElement('canvas');
        offscreen.width = Math.round(src.width * scale);
        offscreen.height = Math.round(src.height * scale);
        // The dashed frame is guidance, not part of the mockup. Redraw without
        // it for the copy, then put it back for the screen.
        this._exporting = true;
        try {
            this.render();
            offscreen.getContext('2d').drawImage(src, 0, 0, offscreen.width, offscreen.height);
        } finally {
            this._exporting = false;
            this.render();
        }

        return new Promise((resolve, reject) => {
            offscreen.toBlob(
                async (blob) => {
                    if (!blob) {
                        reject(new Error('Canvas toBlob failed'));
                        return;
                    }
                    try {
                        resolve(await this.uploadToWorker(blob, this.buildUploadFilename('jpg')));
                    } catch (err) {
                        reject(err);
                    }
                },
                'image/jpeg',
                uploadJpegQuality
            );
        });
    }

    // ─── Rendering ───────────────────────────────────────────────────────────

    render() {
        if (!this.ctx || !this.canvas) {
            debug.warn('render: canvas or context missing');
            return;
        }

        const rect = this.canvas.getBoundingClientRect();

        this.ctx.fillStyle = '#ffffff';
        this.ctx.fillRect(0, 0, rect.width, rect.height);

        // Product image
        if (this.productImage.complete && this.productImage.naturalWidth > 0) {
            const { dx, dy, drawWidth, drawHeight } = this.getImageDrawRect(rect);
            this.ctx.drawImage(this.productImage, dx, dy, drawWidth, drawHeight);
        }

        // Logo
        if (this.logoImage?.complete && this.logoImage?.naturalWidth > 0) {
            const { w, h } = this.getLogoDisplaySize(rect);
            const lx = (this.logoPosition.x / 100) * rect.width;
            const ly = (this.logoPosition.y / 100) * rect.height;

            this.ctx.save();
            this.ctx.shadowColor = this.config.textShadowColor;
            this.ctx.shadowBlur = this.config.textShadowBlur;
            this.ctx.drawImage(this.logoImage, lx - w / 2, ly - h / 2, w, h);
            this.ctx.restore();
        }

        // Text
        const text = this.textInput?.value || '';
        if (text) {
            if (this.isMonogram()) this.syncMonogramPosition(rect);

            const fontSize = this.getFontSize();
            const font = this.getSelectedFont();
            const tx = (this.textPosition.x / 100) * rect.width;
            const ty = (this.textPosition.y / 100) * rect.height;

            if (this.isMonogram()) this.renderDeboss(text, fontSize, tx, ty, rect);
            else this.renderEngraving(text, fontSize, tx, ty, rect);

            if (this.config.debugMode) {
                this.ctx.font = `${fontSize}px ${this.getFontStack()}`;
                this.drawDebugGuides(text, fontSize, font, tx, ty, rect);
            }

            if (!this._exporting && !this.isMonogram() && this._hintAlpha > 0.01) {
                this.drawHint(this._hintAlpha);
            }
        }

        this.updateMoveHandle();
    }

    // ─── Debossed mark ────────────────────────────────────────────────────────

    /**
     * Map the admin's image-relative percentage onto the canvas. The image is
     * contain-fitted, so the same percentage lands on the same bit of leather
     * whether the canvas is wide or tall.
     *
     * @param {DOMRect} rect
     */
    syncMonogramPosition(rect) {
        if (!this.productImage.complete || !this.productImage.naturalWidth || !rect.width || !rect.height) {
            this.textPosition = { ...this.monogramImagePos };
            return;
        }
        const { dx, dy, drawWidth, drawHeight } = this.getImageDrawRect(rect);
        this.textPosition = {
            x: ((dx + (this.monogramImagePos.x / 100) * drawWidth) / rect.width) * 100,
            y: ((dy + (this.monogramImagePos.y / 100) * drawHeight) / rect.height) * 100
        };
    }

    /**
     * Paint the monogram the way the press leaves it: the letters sunk into the
     * leather, either bare or carrying a foil. Two shadow passes on the wall
     * nearest the light and one lit pass on the far wall give the depth; the
     * fill goes down last and covers everything but the walls.
     *
     * @param {string} text
     * @param {number} fontSize
     * @param {number} tx Centre of the monogram, canvas px.
     * @param {number} ty
     * @param {DOMRect} rect
     */
    renderDeboss(text, fontSize, tx, ty, rect) {
        const paint = (context) => this.paintGlyphs(context, text, fontSize, tx, ty);
        const cfg = this.config.deboss;
        const color = this.getSelectedColorKey();
        // The context is scaled by the device pixel ratio, so one device pixel
        // is 1/scale in the units translate() takes.
        const rim = cfg.rimOffset / (this.canvas.width / rect.width);

        // Read the leather before anything is painted over it, so a bare
        // impression takes its tone from the hide and not from its own walls.
        const fill = this.debossFill(color, fontSize, tx, ty, rect);

        // Near wall, in shadow. The second pass at twice the offset is what
        // makes the impression read as deep rather than as a drop shadow.
        this.ctx.save();
        this.ctx.fillStyle = `rgba(0, 0, 0, ${cfg.rimDark * 0.5})`;
        this.ctx.translate(-rim * 2, -rim * 2);
        paint(this.ctx);
        this.ctx.restore();

        this.ctx.save();
        this.ctx.fillStyle = `rgba(0, 0, 0, ${cfg.rimDark})`;
        this.ctx.translate(-rim, -rim);
        paint(this.ctx);
        this.ctx.restore();

        // Far wall, catching the light.
        this.ctx.save();
        this.ctx.fillStyle = `rgba(255, 246, 235, ${cfg.rimLight})`;
        this.ctx.translate(rim, rim);
        paint(this.ctx);
        this.ctx.restore();

        this.ctx.save();
        this.ctx.fillStyle = fill;
        paint(this.ctx);
        this.ctx.restore();
    }

    /**
     * What the floor of the impression is made of. With no foil it is the
     * leather itself, a shade darker where the heat compressed it; with a foil
     * it is the metal, lit from above.
     *
     * paintGlyphs translates to the monogram's centre before filling, so the
     * gradient is expressed around that origin rather than in canvas space.
     *
     * @returns {string|CanvasGradient}
     */
    debossFill(color, fontSize, tx, ty, rect) {
        const cfg = this.config.deboss;
        const foil = cfg.foils[color];

        if (!foil) {
            const leather = this.sampleLeatherUnderText(tx, ty, rect);
            const [h, l, s] = rgbToHsl(leather[0], leather[1], leather[2]);
            const [r, g, b] = hslToRgb(h, Math.min(1, s * 1.1), Math.max(0.03, l - cfg.blindDarken));
            return `rgb(${r}, ${g}, ${b})`;
        }

        const grad = this.ctx.createLinearGradient(0, -fontSize / 2, 0, fontSize / 2);
        grad.addColorStop(0, foil.top);
        grad.addColorStop(0.55, foil.mid);
        grad.addColorStop(1, foil.bottom);
        return grad;
    }

    // ─── Engraved mark ────────────────────────────────────────────────────────

    /**
     * Paint the message the way the laser leaves it, rather than as colored
     * text laid over the photograph. Three passes: an absolute burn tone taken
     * from the leather underneath, the leather's own grain let back through the
     * letters, and a one pixel rim on each side so the mark sits in the surface
     * instead of on it.
     *
     * @param {string} text
     * @param {number} fontSize
     * @param {number} tx Centre of the engraving, canvas px.
     * @param {number} ty
     * @param {DOMRect} rect
     */
    renderEngraving(text, fontSize, tx, ty, rect) {
        const paint = (context) => this.paintGlyphs(context, text, fontSize, tx, ty);

        const leather = this.sampleLeatherUnderText(tx, ty, rect);
        const cfg = this.config.engrave;
        const burn = this.scorchColor(leather);
        const flat = fontSize < cfg.flatBelowPx;

        const usePixels = cfg.pixelPath === 'always' || (cfg.pixelPath !== 'never' && !canvasFilterWorks());
        const blurCss = Math.max(1, fontSize * cfg.haloBlur);
        // Physical-pixel box around the glyphs, with room for the halo's spread
        // and the rims. The pixel path only ever touches this much of the canvas,
        // which is what keeps dragging smooth on a phone.
        this._engraveRegion = usePixels ? this.getEngravingRegion(fontSize, tx, ty, rect, blurCss * 3 + cfg.rimOffset + fontSize * 0.6) : null;

        const off = this.getEngraveLayer(rect);
        if (!off) {
            // No offscreen layer available, so fall back to the flat burn tone.
            this.ctx.save();
            this.ctx.fillStyle = burn;
            paint(this.ctx);
            this.ctx.restore();
            return;
        }

        // The burn layer is built first and from a canvas that still holds only
        // the product. Drawing the rims before this point put them into the
        // grain pass, and the clip then stamped a ghost of each offset copy
        // inside the letters, which at any size above small read as a shadow
        // sitting away from the mark.
        const o = off.getContext('2d');
        o.setTransform(1, 0, 0, 1, 0, 0);
        o.clearRect(0, 0, off.width, off.height);
        o.setTransform(off.width / rect.width, 0, 0, off.height / rect.height, 0, 0);

        o.fillStyle = burn;
        paint(o);

        // Overlay puts the leather's light and shade back into the letters. It
        // spills outside them, which the destination-in pass then trims away.
        //
        // The grain has to be centred on mid grey first. Overlaying the leather
        // straight off the canvas meant that on a dark hide every pixel of the
        // source was dark, so overlay drove the whole fill back down to roughly
        // the leather's own tone and only the rim survived. The mark measured
        // the right contrast and still could not be read, because barely three
        // percent of it was ever reaching the burn color.
        const grain = flat ? null : this.getGrainLayer(rect, leather, cfg);
        if (grain) {
            o.globalCompositeOperation = 'overlay';
            o.globalAlpha = 1;
            o.drawImage(grain, 0, 0, rect.width, rect.height);
        }

        o.globalCompositeOperation = 'destination-in';
        o.globalAlpha = 1;
        o.fillStyle = '#000';
        paint(o);

        o.globalCompositeOperation = 'source-over';

        // Which way the mark went decides how it is lit. A cream halo and a bright
        // rim belong around a mark that is lighter than the hide; put them around
        // a darker one and they lift it straight back toward the leather, which
        // is what kept cognac looking weak while every other color was fine.
        const [, leatherL] = rgbToHsl(leather[0], leather[1], leather[2]);
        const [, burnL] = rgbToHsl(...burn.match(/\d+/g).map(Number));
        const darkerMark = burnL < leatherL;

        if (cfg.halo > 0 && !flat) {
            // Blurred glyphs spread past the sharp ones; the fill then covers the
            // middle and leaves the spread showing as a ring.
            const haloColor = darkerMark ? 'rgb(38, 22, 12)' : 'rgb(255, 246, 235)';
            if (usePixels) {
                this.paintHaloPixels(paint, haloColor, cfg.halo, blurCss, this._engraveRegion);
            } else {
                this.ctx.save();
                this.ctx.filter = `blur(${blurCss.toFixed(2)}px)`;
                this.ctx.globalAlpha = cfg.halo;
                this.ctx.fillStyle = haloColor;
                paint(this.ctx);
                this.ctx.restore();
            }
        }

        // Rims go down now, against the leather, with the fill landing on top so
        // only the one pixel that sits proud of each edge shows.
        // Emphasis swaps with the mark: the shadowed wall leads on a dark burn,
        // the lit one on a pale burn.
        const rimLight = darkerMark ? cfg.rimDark : cfg.rimLight;
        const rimDark = darkerMark ? cfg.rimLight : cfg.rimDark;
        // The context is scaled by the device pixel ratio, so one device pixel
        // is 1/scale in the units translate() takes.
        const rim = cfg.rimOffset / (this.canvas.width / rect.width);

        if (rimLight > 0) {
            this.ctx.save();
            this.ctx.fillStyle = `rgba(255, 246, 235, ${rimLight})`;
            this.ctx.translate(rim, rim);
            paint(this.ctx);
            this.ctx.restore();
        }

        if (rimDark > 0) {
            this.ctx.save();
            this.ctx.fillStyle = `rgba(0, 0, 0, ${rimDark})`;
            this.ctx.translate(-rim, -rim);
            paint(this.ctx);
            this.ctx.restore();
        }

        this.ctx.save();
        this.ctx.drawImage(off, 0, 0, rect.width, rect.height);
        this.ctx.restore();
    }

    /**
     * The glyph geometry, in one place, so the fill, the rims and the mask are
     * guaranteed to land on top of each other.
     *
     * @param {CanvasRenderingContext2D} context
     */
    paintGlyphs(context, text, fontSize, tx, ty) {
        context.save();
        context.font = `${fontSize}px ${this.getFontStack()}`;
        // A monogram is right-aligned on its point. The location coordinate is
        // where the press stops, not where the middle of the initials lands,
        // so at X=90% on a narrow item a centred block put half the monogram
        // past the usable area (Vaidas, Sep 2026). An engraving stays centred:
        // that point is a drag position, not a press stop.
        context.textAlign = this.isMonogram() ? 'right' : 'center';
        context.textBaseline = 'middle';
        // The theme puts letter-spacing on body and Chrome applies an
        // element's inherited spacing to canvas text. The on-page canvas got
        // 0.45 px per letter while the offscreen burn layer, being outside the
        // DOM, got none, so the rims drifted a full glyph away from the fill
        // over a long message. Every context draws with zero, like the DXF.
        if ('letterSpacing' in context) context.letterSpacing = '0px';
        if ('wordSpacing' in context) context.wordSpacing = '0px';

        if (this.isStacked()) {
            // Glyphs stay upright and step along the rotated axis, one line
            // height apart, centred on the drag position.
            const chars = [...text];
            const spacing = fontSize * this.config.stackLineHeightRatio;
            const dir = this.getAdvanceVector();
            chars.forEach((char, index) => {
                const offset = (index - (chars.length - 1) / 2) * spacing;
                context.fillText(char, tx + dir.x * offset, ty + dir.y * offset);
            });
        } else {
            // The whole block turns as one, so at 180° the message reads upside
            // down rather than each letter flipping in place.
            context.translate(tx, ty);
            context.rotate((this.textRotation * Math.PI) / 180);
            context.fillText(text, 0, 0);
        }

        context.restore();
    }

    /**
     * Average the leather the letters actually cover, not the whole photograph,
     * so a mark in a shadowed corner burns differently from one on a lit panel.
     *
     * @returns {[number, number, number]}
     */
    /**
     * Left edge of the drawn text block, in the same units as tx.
     *
     * Everything that reads pixels around the mark has to agree with
     * paintGlyphs about where it sits, and a monogram is anchored by its right
     * edge rather than its middle.
     */
    getTextBoxLeft(tx, w) {
        return this.isMonogram() ? tx - w : tx - w / 2;
    }

    sampleLeatherUnderText(tx, ty, rect) {
        const dpr = this.canvas.width / rect.width || 1;
        const { w, h } = this.getTextDisplaySize();
        const x = Math.max(0, Math.round(this.getTextBoxLeft(tx, w) * dpr));
        const y = Math.max(0, Math.round((ty - h / 2) * dpr));
        const cw = Math.min(this.canvas.width - x, Math.round(w * dpr));
        const ch = Math.min(this.canvas.height - y, Math.round(h * dpr));
        if (cw < 1 || ch < 1) return [128, 110, 96];

        let data;
        try {
            data = this.ctx.getImageData(x, y, cw, ch).data;
        } catch (err) {
            // A tainted canvas would throw. The product images are served with
            // CORS headers so this should not happen, but a neutral mid tan is a
            // better outcome than no engraving at all.
            debug.warn('sampleLeatherUnderText: canvas not readable', { err });
            return [128, 110, 96];
        }

        // Every fourth pixel is plenty for an average and keeps dragging smooth.
        // Pixels are sorted by luminance and only the middle 60% are averaged:
        // when a line crosses a brass lock or a stitch, a plain mean drifted
        // toward the hardware and flipped the mark from a pale burn to a dark
        // one depending on how many letters happened to sit on the metal.
        const px = [];
        for (let i = 0; i < data.length; i += 16) {
            const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
            px.push([lum, data[i], data[i + 1], data[i + 2]]);
        }
        if (!px.length) return [128, 110, 96];
        px.sort((a, b) => a[0] - b[0]);
        const lo = Math.floor(px.length * 0.2);
        const hi = Math.max(lo + 1, Math.ceil(px.length * 0.8));
        let r = 0;
        let g = 0;
        let b = 0;
        let sum = 0;
        let sumSq = 0;
        let n = 0;
        for (let i = lo; i < hi; i++) {
            const [lum, pr, pg, pb] = px[i];
            r += pr;
            g += pg;
            b += pb;
            sum += lum;
            sumSq += lum * lum;
            n++;
        }

        // How much texture this patch actually holds, so the grain pass can match
        // a fixed amount rather than inherit whatever happens to be here.
        const mean = sum / n;
        this._leatherSpread = Math.sqrt(Math.max(0, sumSq / n - mean * mean));

        return [r / n, g / n, b / n];
    }

    /**
     * @param {[number, number, number]} rgb Leather color under the letters.
     * @returns {string} CSS color of the burn.
     */
    scorchColor(rgb) {
        const cfg = this.config.engrave;
        const [, l, s] = rgbToHsl(rgb[0], rgb[1], rgb[2]);

        const sat = Math.min(
            cfg.substrateSaturation * (1 - cfg.saturationFromLeather) + s * cfg.saturationFromLeather,
            cfg.maxSaturation
        );

        // Burn darker if that still lands clear of the floor. A pale hide has the
        // room; a dark one does not, and on those the exposed substrate is the
        // lighter of the two anyway.
        const darker = l - cfg.darkSeparation;
        const light = darker >= cfg.darkFloor ? darker : Math.max(l + cfg.lightSeparation, cfg.minLightness);

        const [r, g, b] = hslToRgb(cfg.substrateHue, sat, Math.max(0.06, Math.min(0.94, light)));
        return `rgb(${r}, ${g}, ${b})`;
    }

    /**
     * The leather's texture, desaturated and rebalanced so its average sits on
     * mid grey. That is what makes it read as grain rather than as a second
     * coat of the leather's own darkness.
     *
     * @param {DOMRect} rect
     * @param {[number, number, number]} leather Average color under the letters.
     * @returns {HTMLCanvasElement|null}
     */
    getGrainLayer(rect, leather, cfg) {
        if (!this.canvas?.width) return null;
        if (!this._grainLayer) this._grainLayer = document.createElement('canvas');
        const layer = this._grainLayer;
        if (layer.width !== this.canvas.width || layer.height !== this.canvas.height) {
            layer.width = this.canvas.width;
            layer.height = this.canvas.height;
        }

        const g = layer.getContext('2d');
        g.setTransform(1, 0, 0, 1, 0, 0);
        g.clearRect(0, 0, layer.width, layer.height);
        g.setTransform(layer.width / rect.width, 0, 0, layer.height / rect.height, 0, 0);

        // Rec. 709 luma of the leather, floored so a near black hide cannot send
        // the brightness multiplier to infinity.
        const luma = Math.max(8, 0.2126 * leather[0] + 0.7152 * leather[1] + 0.0722 * leather[2]);
        const lift = 128 / luma;
        // The brightness lift scales the patch's own spread along with it, so the
        // contrast needed to reach the target is worked out after that.
        const spread = Math.max(0.5, (this._leatherSpread || 12) * lift);
        const contrast = Math.max(
            cfg.grainContrastMin,
            Math.min(cfg.grainContrastMax, cfg.grainTarget / spread)
        );
        const region = this._engraveRegion;
        if (region) {
            // Same maths as the filter string below, done by hand: Rec. 709
            // luma, multiplied by the lift, then contrast about mid grey. CSS
            // shorthand filters work in sRGB, so no linearising here either.
            // Only the glyph region is touched; destination-in trims the rest.
            const src = this.ctx.getImageData(region.x, region.y, region.w, region.h);
            const d = src.data;
            const offset = 128 * (1 - contrast);
            for (let i = 0; i < d.length; i += 4) {
                const v = (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]) * lift * contrast + offset;
                d[i] = d[i + 1] = d[i + 2] = v;
            }
            g.putImageData(src, region.x, region.y);
            return layer;
        }
        g.filter = `grayscale(1) brightness(${lift.toFixed(3)}) contrast(${contrast.toFixed(3)})`;
        g.drawImage(this.canvas, 0, 0, rect.width, rect.height);
        g.filter = 'none';
        return layer;
    }

    /**
     * Axis-aligned box around the engraving in physical canvas pixels, padded
     * and clamped to the canvas. The pixel path reads and writes only this.
     */
    getEngravingRegion(fontSize, tx, ty, rect, padCss) {
        const { w, h } = this.getTextDisplaySize();
        const sx = this.canvas.width / rect.width;
        const sy = this.canvas.height / rect.height;
        const left = this.getTextBoxLeft(tx, w);
        const x0 = Math.max(0, Math.floor((left - padCss) * sx));
        const y0 = Math.max(0, Math.floor((ty - h / 2 - padCss) * sy));
        const x1 = Math.min(this.canvas.width, Math.ceil((left + w + padCss) * sx));
        const y1 = Math.min(this.canvas.height, Math.ceil((ty + h / 2 + padCss) * sy));
        return { x: x0, y: y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0), sx, sy };
    }

    /**
     * The halo without filter: blur() support. The glyphs are painted into an
     * offscreen canvas the size of the region, the alpha is box-blurred to the
     * same radius the filter would have used, and the result is composited back
     * at the halo's opacity.
     */
    paintHaloPixels(paint, color, alpha, blurCss, region) {
        if (!this._haloLayer) this._haloLayer = document.createElement('canvas');
        const layer = this._haloLayer;
        if (layer.width !== region.w || layer.height !== region.h) {
            layer.width = region.w;
            layer.height = region.h;
        }
        const c = layer.getContext('2d');
        c.setTransform(1, 0, 0, 1, 0, 0);
        c.clearRect(0, 0, region.w, region.h);
        c.setTransform(region.sx, 0, 0, region.sy, -region.x, -region.y);
        c.fillStyle = color;
        paint(c);
        const img = c.getImageData(0, 0, region.w, region.h);
        boxBlurAlpha(img.data, region.w, region.h, Math.round(blurCss * region.sx));
        c.setTransform(1, 0, 0, 1, 0, 0);
        c.putImageData(img, 0, 0);

        this.ctx.save();
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.globalAlpha = alpha;
        this.ctx.drawImage(layer, region.x, region.y);
        this.ctx.restore();
    }

    /** Offscreen layer the burn is composed on, sized to the canvas and reused. */
    getEngraveLayer(rect) {
        if (!this.canvas?.width) return null;
        if (!this._engraveLayer) this._engraveLayer = document.createElement('canvas');
        const layer = this._engraveLayer;
        if (layer.width !== this.canvas.width || layer.height !== this.canvas.height) {
            layer.width = this.canvas.width;
            layer.height = this.canvas.height;
        }
        return layer.width && layer.height ? layer : null;
    }

    // ─── Event / variant handlers ─────────────────────────────────────────────

    handleResize() {
        this.resizeCanvas();
        this.bindControlsScroll();
    }

    handleVariantChange(e) {
        const variantId = e.detail?.variant?.id;
        if (!variantId) return;
        this.updateButtonVisibility(variantId);

        // Nothing applied yet, so the change costs nothing.
        if (!this._appliedPropertyKeys?.length) return;
        if (String(variantId) === String(this._appliedVariantId)) return;

        // The engraving was drawn on the old color's photo and the mockup on
        // the order is that drawing. Shopify order #ES#1511 went out with a
        // green bag and a black mockup because the properties stayed on the
        // form through a color change and nothing asked the customer to look
        // again.
        //
        // If the new variant cannot be personalized at all there is nothing to
        // re-confirm against, so the engraving comes off rather than riding
        // along on a form the customer can no longer see.
        if (!this.variantHasLocations(variantId)) {
            debug.log('handleVariantChange: new variant has no locations, clearing', { variantId });
            this.clearPersonalization();
            return;
        }

        this._appliedVariantId = String(variantId);
        this._needsRevalidation = true;
        // The drag position is a percentage of the photo and both photos frame
        // the same product, so it still means what the customer chose. They
        // confirm it against the new color instead of starting again.
        this._preserveTextPosition = true;

        debug.log('handleVariantChange: personalization needs re-confirming', { variantId });
        document.dispatchEvent(new CustomEvent('personalization:stale'));
    }

    /** Variant ids the merchant has set up engraving locations for. */
    variantHasLocations(variantId) {
        const jsonScript = document.querySelector(
            '[data-personalization-button] script[data-personalization-variants]'
        );
        if (!jsonScript) return false;

        try {
            return JSON.parse(jsonScript.textContent).includes(String(variantId));
        } catch (error) {
            debug.error('variantHasLocations: failed to parse variants JSON', error);
            return false;
        }
    }

    /**
     * Swallow Add to Cart while the applied engraving belongs to a different
     * variant, and send the customer back into the dialog to look at it.
     * Capture phase so it lands before the theme's own form handler.
     */
    handleAddToCartGuard(e) {
        if (!this._needsRevalidation) return;
        if (!e.target.closest?.('[data-add-to-cart]')) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        debug.log('handleAddToCartGuard: blocked, opening dialog to re-confirm');
        this.openModal();
    }

    /** The same block for a keyboard submit, which never clicks the button. */
    handleProductFormSubmitGuard(e) {
        if (!this._needsRevalidation) return;
        if (!e.target.matches?.('[data-product-form]')) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.openModal();
    }

    updateButtonVisibility(variantId) {
        const buttonWrapper = document.querySelector('[data-personalization-button]');
        if (!buttonWrapper) return;

        buttonWrapper.style.display = this.variantHasLocations(variantId) ? '' : 'none';
    }

    handleLocationChange(e) {
        if (e.target.matches('[data-location-select]')) this.selectLocation(e.target);
    }

    selectLocation(input) {
        const url = input.dataset.url;
        const width = input.dataset.width ? parseFloat(input.dataset.width) : null;

        if (!url) {
            debug.error('selectLocation: no data-url on input', {
                value: input.value,
                dataset: { ...input.dataset }
            });
            return;
        }

        // Reset text position only when the location actually changes.
        // The modal-open observer re-fires initLocationSelector on every
        // re-open, so an unconditional reset would wipe the user's drag
        // position whenever they reopen the modal to edit applied work.
        const locationChanged = this._currentLocationUrl !== url;
        this._currentLocationUrl = url;
        this.currentImageCm = width;
        // A color change swaps the photo for the same spot on the same
        // product, so the customer's drag still applies and resetting it would
        // throw away work they are only being asked to check.
        const preserve = this._preserveTextPosition;
        this._preserveTextPosition = false;
        if (locationChanged && !preserve) {
            // The new photo is not here yet, so this parks the text and the
            // real spot is worked out when it lands.
            this.textPosition = { ...this.config.defaultTextPosition };
            this._resetPositionOnLoad = true;
        }

        // Where the press lands on this location, set in the admin as a
        // percentage of the image. Unset means the middle.
        const mx = parseFloat(input.dataset.monogramX);
        const my = parseFloat(input.dataset.monogramY);
        this.monogramImagePos = {
            x: Number.isFinite(mx) ? Math.min(100, Math.max(0, mx)) : 50,
            y: Number.isFinite(my) ? Math.min(100, Math.max(0, my)) : 50
        };

        debug.log('selectLocation:', {
            location: input.value,
            url,
            widthCm: width,
            locationChanged,
            monogramPos: this.monogramImagePos
        });

        this.updateSizeSlider();

        const label = this.querySelector(`label[for="${input.id}"]`);
        const thumbnailImg = label?.querySelector('img');

        this.loadProductImage(url, thumbnailImg);
    }

    // ─── Utility ──────────────────────────────────────────────────────────────

    sanitizeInput(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }

    closeModal() {
        this.hideSizeHint();
        // Clear edit-mode state so a subsequent open (without ?personalize=)
        // doesn't still target the old cart line.
        this.editLineKey = null;
        this.editQuantity = null;
        this.editVariantId = null;

        const modal = this.closest('modal-dialog');
        if (modal && typeof modal.close === 'function') {
            modal.close();
        } else {
            debug.error('closeModal: modal-dialog element or close() method not found');
        }
    }

    /**
     * Character messages get their own line. showWarning shares an element with
     * the length counter, which re-toggles it on every keystroke and would hide
     * this again before anyone read it.
     *
     * @param {string} message
     */
    showCharsetWarning(message) {
        if (!message || !this.charsetWarning) return;
        this.charsetWarning.textContent = message;
        this.charsetWarning.classList.remove('hidden');
        clearTimeout(this._charsetWarningTimer);
        this._charsetWarningTimer = setTimeout(() => this.charsetWarning.classList.add('hidden'), 6000);
        debug.warn('charset:', message);
    }

    // ─── Position check ──────────────────────────────────────────────────────

    /**
     * True when Apply should stop and ask about the position first.
     *
     * It asks on every Apply. This started out conditional, skipped once the
     * customer had dragged the text and answered once per session, but Vaidas
     * asked for it unconditionally (Asana, Sep 2026) so that nobody applies an
     * engraving without being shown where it will sit.
     *
     * A monogram is still exempt: the press position is fixed per product and
     * there is nothing to drag.
     *
     * _positionConfirmed is a one-shot rather than a latch. Confirming
     * resubmits the form, which re-enters handleApply, so the flag exists only
     * to let that one resubmit through and is spent on the way past. Without
     * spending it the dialog would never reappear; without setting it at all
     * the confirm would reopen the dialog it just closed, forever.
     */
    shouldCheckPosition() {
        if (this.isMonogram() || !this.positionCheck) return false;
        if (this._positionConfirmed) {
            this._positionConfirmed = false;
            return false;
        }
        return true;
    }

    showPositionCheck() {
        if (!this.positionCheck) return;
        this.positionCheck.hidden = false;
        this.positionCheck.classList.remove('hidden');
        this.positionCheck.classList.add('flex');
        // The frame goes back up with the question, so the thing being asked
        // about is the thing outlined on the canvas.
        this._hintPinned = true;
        this.setHint(true);
        // The panel takes focus, not the confirm button: .btn--primary slides
        // its skewed fill away on :focus just as it does on :hover, so a
        // focused button looks like one the cursor is already over.
        this.positionCheckPanel?.focus();
    }

    hidePositionCheck() {
        if (!this.positionCheck) return;
        this.positionCheck.hidden = true;
        this.positionCheck.classList.add('hidden');
        this.positionCheck.classList.remove('flex');
    }

    /** Happy with it: apply, and do not ask again this session. */
    handlePositionConfirm() {
        this._positionConfirmed = true;
        this.hidePositionCheck();
        this.form?.requestSubmit
            ? this.form.requestSubmit()
            : this.handleApply(new Event('submit', { cancelable: true }));
    }

    /** Wants to move it: back to the canvas with the frame up. */
    handlePositionMove() {
        this.hidePositionCheck();
        this._hintPinned = true;
        this.setHint(true);
        // Only worth scrolling below md, where the canvas sits above the
        // controls and may be off screen. On desktop it is already in view.
        if (!window.matchMedia('(min-width: 768px)').matches) {
            this.canvas?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    showWarning(message) {
        if (this.charWarning) {
            this.charWarning.textContent = message;
            this.charWarning.classList.remove('hidden');
            setTimeout(() => this.charWarning.classList.add('hidden'), 5000);
        }
        debug.warn('Validation warning:', message);
    }

    async waitForFonts() {
        if (document.fonts?.ready) {
            try {
                await document.fonts.ready;
            } catch (error) {
                debug.warn('waitForFonts: document.fonts.ready rejected', error);
            }
        }
    }

    getSelectedFont() {
        return this.querySelector('input[name="font"]:checked')?.value || 'serif';
    }

    /**
     * CSS font-family list for the canvas: the chosen font first, then the other
     * engraving fonts. The DXF composes glyphs in this same order, so a symbol
     * the chosen font lacks is drawn from the same substitute in the preview and
     * in the file the laser receives. The matching stack for the measuring
     * element lives on the .font-* classes in personalization-popup.liquid.
     */
    getFontStack() {
        const selected = this.getSelectedFont();
        const all = Array.from(this.fontInputs).map((input) => input.value);
        const ordered = [selected, ...all.filter((name) => name !== selected)];
        return [...ordered.map((name) => `"${name}"`), 'serif'].join(', ');
    }

    getFontSize() {
        return this.currentFontSize;
    }

    /** On-screen pixels per centimetre of product, from the displayed image. */
    pxPerCmNow() {
        const rect = this.canvas?.getBoundingClientRect();
        if (this.currentImageCm && rect?.width) {
            return this.getDisplayedImageWidth(rect) / this.currentImageCm;
        }
        return this.config.pixelsPerCm;
    }

    mmToPx(mm) {
        return (mm / 10) * this.pxPerCmNow();
    }

    pxToMm(px) {
        return (px / this.pxPerCmNow()) * 10;
    }

    clampMm(mm) {
        const { minFontSizeMm, maxFontSizeMm, fontSizeStepMm } = this.config;
        const snapped = Math.round(mm / fontSizeStepMm) * fontSizeStepMm;
        return Math.min(maxFontSizeMm, Math.max(minFontSizeMm, snapped));
    }

    /**
     * How much of the em the font's capitals actually fill.
     *
     * Measured rather than assumed: Georgia comes out at 0.693 and the generic
     * serif fallback at 0.662, so reading it before the webfont has loaded
     * would size every monogram off the wrong metric. Cached only once the
     * real face is in use.
     */
    getCapHeightRatio() {
        const font = this.getSelectedFont();
        if (this._capHeightRatioFor === font) return this._capHeightRatio;

        const ctx = document.createElement('canvas').getContext('2d');
        const probe = 400;
        ctx.font = `${probe}px ${this.getFontStack()}`;
        const ascent = ctx.measureText('H').actualBoundingBoxAscent;
        if (!ascent) return this.config.monogram.fallbackCapHeightRatio;

        const ratio = ascent / probe;
        // Only worth remembering once the face it was measured from is the
        // one that will be drawn.
        if (document.fonts?.check(`${probe}px ${font}`)) {
            this._capHeightRatio = ratio;
            this._capHeightRatioFor = font;
        }
        return ratio;
    }

    /**
     * Size the monogram so the pressed capitals come out at the height the
     * press actually makes, rather than setting the font to that number.
     *
     * Assigned straight rather than through setFontSizeMm: that snaps to the
     * slider's 0.5mm step, and this is a fixed size with no slider behind it.
     */
    applyMonogramSize() {
        this.currentFontSizeMm = this.config.monogram.capHeightMm / this.getCapHeightRatio();
        this.syncFontSizePx();
    }

    /** The size lives in mm; the px twin is what every drawing call reads. */
    setFontSizeMm(mm) {
        this.currentFontSizeMm = this.clampMm(Number.isFinite(mm) ? mm : this.config.defaultFontSizeMm);
        this.syncFontSizePx();
    }

    /** Re-derive px from mm after anything that changes the canvas scale. */
    syncFontSizePx() {
        this.currentFontSize = this.mmToPx(this.currentFontSizeMm);
    }

    // ─── Size constraint helpers ──────────────────────────────────────────────

    initializeSizeControls() {
        if (!this.sizeSlider) return;
        // Pull every engraving font in now rather than when first drawn, so
        // the limits are computed with the real glyph widths as early as
        // possible instead of the fallback's.
        Array.from(this.fontInputs || []).forEach((input) => this.ensureFontLoaded(input.value));
        this.sizeSlider.min = this.config.minFontSizeMm;
        this.sizeSlider.max = this.config.maxFontSizeMm;
        this.sizeSlider.step = this.config.fontSizeStepMm;
        this.updateSizeSlider();
        this.updateCharacterCounter();
    }

    getMaxEngravingWidthPixels() {
        return this.pxPerCmNow() * this.config.maxEngravingWidthCm;
    }

    calculateMaxCharacters(fontSize) {
        // The press takes five pieces of type, whatever the size.
        if (this.isMonogram()) return this.config.monogram.maxChars;

        const maxWidthPixels = this.getMaxEngravingWidthPixels();

        // Stacked letters each take a full line height, so the run length is
        // driven by the character count rather than by glyph widths.
        if (this.isStacked()) {
            const perChar = fontSize * this.config.stackLineHeightRatio;
            return Math.max(this.config.minCharacterCount, Math.floor(maxWidthPixels / perChar));
        }

        // Every character is counted at the width of the widest glyph the
        // font offers, so the limit depends only on the size and the font:
        // one number that holds still while the customer types. Measuring the
        // typed letters instead made the maximum jump on every keystroke.
        // Narrow text is cut off a little earlier than 13 cm strictly needs;
        // wide text can never exceed it.
        const perChar = this.widestGlyphWidth(fontSize, this.getSelectedFont());
        return Math.max(this.config.minCharacterCount, Math.floor(maxWidthPixels / perChar));
    }

    /**
     * Width of the widest engravable character in a font at a size. Capitals,
     * digits and the picker symbols are the candidates; lowercase is never
     * wider than the widest capital in these faces.
     */
    widestGlyphWidth(fontSize, fontFamily) {
        const candidates = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'.split('').concat(PICKER_SYMBOLS);
        let widest = 0;
        for (const ch of candidates) {
            widest = Math.max(widest, this.measureTextWidth(ch, fontSize, fontFamily));
        }
        return widest > 0 ? widest : fontSize * this.config.averageCharWidthRatio;
    }

    calculateMaxFontSize(textLength) {
        if (this.isStacked()) {
            const chars = Math.max(
                this.config.minCharacterCount,
                this.textInput ? this.textInput.value.length : textLength
            );
            const fitted = this.getMaxEngravingWidthPixels() / (chars * this.config.stackLineHeightRatio);
            return Math.floor(Math.min(this.mmToPx(this.config.maxFontSizeMm), Math.max(this.mmToPx(this.config.minFontSizeMm), fitted)));
        }

        if (!this.textInput) {
            const effectiveLength = Math.max(this.config.minCharacterCount, textLength);
            const maxFontSize =
                this.getMaxEngravingWidthPixels() / (effectiveLength * this.config.averageCharWidthRatio);
            return Math.floor(
                Math.min(this.mmToPx(this.config.maxFontSizeMm), Math.max(this.mmToPx(this.config.minFontSizeMm), maxFontSize))
            );
        }

        const maxWidthPixels = this.getMaxEngravingWidthPixels();
        const font = this.getSelectedFont();

        // Same rule as the character limit: the typed count at the widest
        // glyph's width, so the size ceiling depends on how many characters
        // there are, not on which ones. Width is linear in size, so the
        // largest size that fits is a ratio.
        const count = Math.max(this.config.minCharacterCount, this.textInput.value.length);
        const widthAt100 = count * this.widestGlyphWidth(100, font);
        const fitted = widthAt100 > 0 ? (maxWidthPixels / widthAt100) * 100 : this.mmToPx(this.config.maxFontSizeMm);
        const result = Math.min(this.mmToPx(this.config.maxFontSizeMm), Math.max(this.mmToPx(this.config.minFontSizeMm), fitted));

        return result;
    }

    updateCharacterCounter() {
        if (!this.charCounter) return;

        const currentLength = this.textInput?.value.length || 0;
        const maxChars = this.calculateMaxCharacters(this.currentFontSize);

        this.charCounter.textContent = `${currentLength}/${maxChars}`;

        if (this.charWarning) {
            this.charWarning.classList.toggle('hidden', currentLength <= maxChars);
        }
    }

    /**
     * The box that actually scrolls behind the Apply bar. It differs by width:
     * the dialog itself below md, the controls column from md up, so it is
     * resolved rather than assumed.
     */
    getControlsScroller() {
        let el = this.applyBtn?.parentElement;
        while (el && el !== document.body) {
            const cs = getComputedStyle(el);
            if (/(auto|scroll)/.test(cs.overflowY) && el.scrollHeight > el.clientHeight + 1) return el;
            el = el.parentElement;
        }
        return null;
    }

    /**
     * The bar's shadow is only there to separate it from content passing
     * underneath, so it fades out once the scroll has run out and there is
     * nothing left to pass. Set inline because it has to beat the class.
     */
    updateApplyBarShadow() {
        const bar = this.applyBtn?.parentElement;
        const scroller = this._controlsScroller;
        if (!bar) return;
        if (!scroller) {
            bar.style.boxShadow = 'none';
            return;
        }
        const atEnd = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 2;
        bar.style.boxShadow = atEnd ? 'none' : '';
    }

    /** Re-resolves the scroller, since a resize can hand it to another element. */
    bindControlsScroll() {
        const next = this.getControlsScroller();
        if (next !== this._controlsScroller) {
            this._controlsScroller?.removeEventListener('scroll', this.handleControlsScroll);
            this._controlsScroller = next;
            this._controlsScroller?.addEventListener('scroll', this.handleControlsScroll, {
                passive: true
            });
        }
        this.updateApplyBarShadow();
    }

    handleControlsScroll() {
        this.updateApplyBarShadow();
    }

    /**
     * The note that says a smaller size buys more characters.
     *
     * Opened two ways: by the message field when it refuses a character, and by
     * the info button beside Text size. One element, anchored to whichever
     * asked for it, because the advice is the same in both places.
     *
     * @param {HTMLElement} anchor - Element to sit under.
     */
    showSizeHint(anchor) {
        if (!this.sizeHint || !anchor) return;
        const form = this.querySelector('form');
        if (!form) return;

        this.sizeHint.classList.remove('hidden');

        const gap = this.config.sizeHintGap;
        const formRect = form.getBoundingClientRect();
        const anchorRect = anchor.getBoundingClientRect();
        const { offsetWidth: width, offsetHeight: height } = this.sizeHint;

        // Clamped to the form so it cannot hang off the right edge on a phone.
        const left = Math.max(
            0,
            Math.min(anchorRect.left - formRect.left, form.clientWidth - width)
        );

        // Below the anchor unless there is no room left, in which case it goes
        // above and the arrow moves to the other edge. The floor is the dialog,
        // since on desktop the controls column scrolls inside a box that ends
        // well above the bottom of the screen, and on a phone it is the Apply
        // bar: that is sticky, so there is technically room under it and the
        // note would otherwise sit over the button.
        const applyWrap = this.applyBtn?.parentElement;
        const stickyApply =
            applyWrap && getComputedStyle(applyWrap).position === 'sticky'
                ? applyWrap.getBoundingClientRect().top
                : Infinity;
        const dialogBottom =
            this.closest('.modal__content')?.getBoundingClientRect().bottom ?? window.innerHeight;
        const floor = Math.min(dialogBottom, stickyApply);
        const below = anchorRect.bottom + gap + height <= floor;

        this.sizeHint.style.left = `${left}px`;
        this.sizeHint.style.top = below
            ? `${anchorRect.bottom - formRect.top + gap}px`
            : `${anchorRect.top - formRect.top - height - gap}px`;

        if (this.sizeHintArrow) {
            // Pointing at the middle of whatever opened it, kept inside the
            // note's own corners so it never floats off the rounded edge.
            const anchorCenter = anchorRect.left + anchorRect.width / 2 - formRect.left;
            const arrowLeft = Math.max(10, Math.min(anchorCenter - left - 5, width - 20));
            this.sizeHintArrow.style.left = `${arrowLeft}px`;
            this.sizeHintArrow.style.top = below ? '-5px' : '';
            this.sizeHintArrow.style.bottom = below ? '' : '-5px';
        }

        this.sizeHintBtn?.setAttribute('aria-expanded', 'true');

        debug.log('showSizeHint:', { anchor: anchor.tagName, left, below });
    }

    hideSizeHint() {
        if (!this.sizeHint || this.sizeHint.classList.contains('hidden')) return;
        this.sizeHint.classList.add('hidden');
        this.sizeHintBtn?.setAttribute('aria-expanded', 'false');
    }

    /**
     * Shown only when dropping a size would actually help. At the smallest
     * size there is nothing left to trade, so the message would be advice the
     * customer cannot act on.
     */
    maybeOfferSmallerSize(anchor) {
        if (this.isMonogram()) return;
        if (this.currentFontSizeMm <= this.config.minFontSizeMm) {
            debug.log('maybeOfferSmallerSize: already at the smallest size');
            return;
        }
        this.showSizeHint(anchor);
    }

    handleSizeHintToggle(e) {
        e.preventDefault();
        if (this.sizeHint && !this.sizeHint.classList.contains('hidden')) this.hideSizeHint();
        else this.showSizeHint(this.sizeHintBtn);
    }

    /** Clicking anywhere that is not the note or its button closes it. */
    handleSizeHintOutsideClick(e) {
        if (!this.sizeHint || this.sizeHint.classList.contains('hidden')) return;
        if (e.target.closest('[data-size-hint]') || e.target.closest('[data-size-hint-btn]')) return;
        this.hideSizeHint();
    }

    updateSizeSlider() {
        if (!this.sizeSlider) return;

        this.syncFontSizePx();
        const maxSize = this.calculateMaxFontSize(this.config.minCharacterCount);
        const step = this.config.fontSizeStepMm;
        const maxMm = Math.max(
            this.config.minFontSizeMm,
            Math.min(this.config.maxFontSizeMm, Math.floor(this.pxToMm(maxSize) / step) * step)
        );
        this.sizeSlider.max = maxMm;

        debug.log('updateSizeSlider:', { maxSize, maxMm, currentFontSizeMm: this.currentFontSizeMm });

        if (this.currentFontSizeMm > maxMm) {
            this.setFontSizeMm(maxMm);
        }
        this.sizeSlider.value = this.currentFontSizeMm;
    }

    findFontSizeForWidth(text, fontFamily, targetWidth) {
        // Width is linear in size, so the size that keeps the same width in
        // the new font is a ratio; the old whole-pixel search rounded
        // differently on each device.
        const widthAt100 = this.measureTextWidth(text, 100, fontFamily);
        const fitted = widthAt100 > 0 ? (targetWidth / widthAt100) * 100 : this.currentFontSize;
        const clamped = Math.min(this.mmToPx(this.config.maxFontSizeMm), Math.max(this.mmToPx(this.config.minFontSizeMm), fitted));
        return Math.min(clamped, this.calculateMaxFontSize(text.length));
    }

    /**
     * Width of the text at a size, measured once per font and text at a
     * reference size and scaled. Measuring at the actual on-screen size gave
     * a different answer on every device: at the 4-8 px a small engraving
     * occupies on a phone the browser rounds glyph advances coarsely, so the
     * character limit came out 33 on one screen and 35 on another for the
     * same millimetres. Glyph advances scale linearly with font size, so one
     * measurement at 100 px is exact for every size and every device.
     */
    measureTextWidth(text, fontSize, fontFamily) {
        const REF = 100;
        const key = `${fontFamily}|${text}`;
        if (this._measureCache.has(key)) return (this._measureCache.get(key) * fontSize) / REF;

        const fontClass = 'font-' + fontFamily.toLowerCase().replace(/\s+/g, '-');
        const div = document.createElement('div');
        div.style.cssText =
            'position:absolute;visibility:hidden;white-space:nowrap;pointer-events:none;top:-9999px;left:-9999px;letter-spacing:0;word-spacing:0;font-size:' +
            REF +
            'px';
        div.className = fontClass;
        div.textContent = text;

        let width;
        try {
            document.body.appendChild(div);
            void div.offsetWidth;
            width = div.getBoundingClientRect().width;
        } finally {
            document.body.removeChild(div);
        }

        // Only cache widths measured with the real font. Before the webfont
        // has arrived the div is laid out in the fallback (Georgia for
        // Engravers MT), whose glyphs are narrower, and a cached fallback width
        // let the customer type 31 characters where 27 fit. Once the font
        // lands, everything that depends on widths is recomputed.
        if (this.isFontReady(fontFamily)) {
            if (this._measureCache.size > 500) this._measureCache.clear();
            this._measureCache.set(key, width);
        } else {
            this.ensureFontLoaded(fontFamily);
        }
        return (width * fontSize) / REF;
    }

    /**
     * Whether a family's @font-face has actually loaded. document.fonts.check()
     * is not usable for this: it answers true for a face that has not been
     * requested yet and for one that failed, which is exactly when the
     * measurement is wrong. A family with no @font-face (a system font) is
     * always ready.
     */
    isFontReady(fontFamily) {
        if (!document.fonts) return true;
        let declared = false;
        for (const face of document.fonts) {
            if (face.family.replace(/^["']|["']$/g, '') !== fontFamily) continue;
            declared = true;
            if (face.status === 'loaded') return true;
        }
        return !declared;
    }

    /** Request a family once and recompute the size limits when it arrives. */
    ensureFontLoaded(fontFamily) {
        if (!document.fonts || this._fontWaits?.has(fontFamily)) return;
        (this._fontWaits ||= new Set()).add(fontFamily);
        document.fonts.load(`100px "${fontFamily}"`).finally(() => {
            this._fontWaits.delete(fontFamily);
            this._measureCache.clear();
            this.updateSizeSlider();
            this.updateCharacterCounter();
            this.render();
        });
    }

    // ─── Debug guides ─────────────────────────────────────────────────────────

    drawDebugGuides(text, fontSize, font, textX, textY, canvasRect) {
        const canvasTextWidth = this.ctx.measureText(text).width;
        const domTextWidth = this.measureTextWidth(text, fontSize, font);
        const maxWidthPixels = this.config.maxEngravingWidthCm * this.config.pixelsPerCm;

        this.ctx.save();
        this.ctx.strokeStyle = '#00ff00';
        this.ctx.lineWidth = 2;
        this.ctx.setLineDash([5, 5]);

        const lineY = textY + fontSize / 2 + 20;
        this.ctx.beginPath();
        this.ctx.moveTo(textX - maxWidthPixels / 2, lineY);
        this.ctx.lineTo(textX + maxWidthPixels / 2, lineY);
        this.ctx.stroke();

        this.ctx.strokeStyle = '#ff0000';
        this.ctx.lineWidth = 1;
        this.ctx.setLineDash([]);
        this.ctx.strokeRect(textX - canvasTextWidth / 2, textY - fontSize / 2, canvasTextWidth, fontSize);

        this.ctx.strokeStyle = '#0000ff';
        this.ctx.lineWidth = 2;
        this.ctx.setLineDash([3, 3]);
        this.ctx.strokeRect(textX - domTextWidth / 2, textY - fontSize / 2, domTextWidth, fontSize);

        this.ctx.fillStyle = '#000000';
        this.ctx.font = '12px monospace';
        this.ctx.textAlign = 'left';
        this.ctx.fillText(
            `Canvas: ${canvasTextWidth.toFixed(1)}px | DOM: ${domTextWidth.toFixed(1)}px | Max: ${maxWidthPixels.toFixed(1)}px`,
            10,
            canvasRect.height - 10
        );

        if (domTextWidth > maxWidthPixels) {
            this.ctx.fillStyle = '#ff0000';
            this.ctx.fillText(
                `⚠️ TEXT TOO WIDE! (${(domTextWidth - maxWidthPixels).toFixed(1)}px over)`,
                10,
                canvasRect.height - 30
            );
        } else {
            this.ctx.fillStyle = '#00aa00';
            this.ctx.fillText(
                `✓ Within limit (${(maxWidthPixels - domTextWidth).toFixed(1)}px remaining)`,
                10,
                canvasRect.height - 30
            );
        }

        this.ctx.restore();
    }
}

customElements.define('personalize-product', PersonalizeProduct);
